import { Injectable, OnDestroy } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, EMPTY, forkJoin, Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, debounceTime, map, skip, switchMap, tap } from 'rxjs/operators';

import { RouteModules } from '../../enums/route-modules.enum';
import { LegFiJwtService } from '../auth/legfi-jwt.service';
import { OrgContextService } from '../organization/org-context.service';
import { EchoService } from '../echo.service';
import { USER_PREFERENCE_KEYS, UserPreferencesService } from '../user-preferences.service';
import { UserPreferencesApiService } from '../user-preferences-api.service';
import {
    AiConversationStreamHandle,
    GableStreamService,
    AiStreamEvent,
    GablePendingQuestions,
} from './gable-stream.service';
import {
    ActionPreviewEdits,
    GableAttachmentRef,
    GableDismissReason,
    GableService,
    GableCitation,
    GableConversationMessage,
    GableConversationListResponse,
    GableConversationMutationResponse,
    GableConversationScope,
    GableConversationScopeRef,
    GableConversationSort,
    GableConversationSummary,
    GableFeedback,
    GablePendingProposal,
    GableQuestionAnswer,
    GableToolCard,
    CARD_PRODUCING_TOOLS,
    isLedgerIntelligenceCardPayload,
} from './gable.service';
import { GableClientErrorReporterService } from './gable-client-error-reporter.service';
import { GableScopeService } from './gable-scope.service';
import { GableWorkflowRun, GableWorkflowsService } from './gable-workflows.service';
import { runFinishDetail } from './gable-workflow-format';
import { GrowlerService } from '../../components/shared/growler/growler.service';
import type { GableActivityFilter } from '../../components/gable/gable-activity-panel/gable-activity-panel.component';

/**
 * Per-message chat row rendered inside the Gable drawer. Mirrors the shape
 * previously local to `GablePageComponent` — kept here verbatim so the drawer
 * host stays a thin view over service state.
 */
export interface GableChatMessage {
    role: 'user' | 'assistant' | 'tool';
    content: string;
    citations?: GableCitation[];
    toolsUsed?: string[];
    timestamp: Date;
    alert?: 'success' | 'warn' | 'info';
    messageId?: number;
    /** Per-conversation order from the server; the gap recovery asks for what follows it. */
    sequence?: number;
    streamState?: 'streaming' | 'complete' | 'failed' | 'cancelled';
    toolName?: string;
    toolCallId?: string;
    toolOk?: boolean;
    thoughts?: GableThought[];
    thoughtsCollapsed?: boolean;
    longRunNotice?: string;
    streamStartedAt?: number;
    streamEndedAt?: number;
    thinkingContent?: string;
    interactionId?: number;
    toolCards?: GableToolCard[];
    /**
     * Sandbox deliverable (SD-7240) lifecycle marker. When set to
     * `'generating'`, this row renders a small spinner + the latest
     * `sandboxLabel` milestone text (the "watch it work" timeline) instead
     * of a chat bubble. On `ai.sandbox.ready` the row is converted into a
     * normal deliverable `toolCards` row (marker cleared); on
     * `ai.sandbox.failed` the row is removed and the error surfaced.
     */
    sandboxState?: 'generating';
    /** Latest milestone label for a `sandboxState: 'generating'` row. */
    sandboxLabel?: string;
    /**
     * Stable per-run key used to track / replace the pending sandbox card —
     * the broadcast `runId` when present, else the legacy
     * `sandbox-{conversationId}` fallback. Multiple pending rows (one per
     * concurrent run) may coexist in the messages array.
     */
    sandboxId?: string;
    /** Files sent with a user turn — render as chips on the bubble. */
    attachments?: GableChatAttachment[];
}

/** One attached file: an upload (`uploadedFileId`) or a library document (`documentId`). */
export interface GableChatAttachment {
    uploadedFileId?: number;
    documentId?: number;
    fileName: string;
}

export function gableAttachmentKey(attachment: GableChatAttachment): string {
    return attachment.documentId ? `doc-${attachment.documentId}` : `upload-${attachment.uploadedFileId}`;
}

export interface GableThought {
    id: string;
    label: string;
    ok?: boolean;
    /**
     * 'tool' — structural tool-call row.
     * 'summary' — layperson-safe paraphrase of one phase of model reasoning,
     *             produced by the Haiku summarizer + denylist pipeline.
     * 'narration' — what the model wrote before a tool call, moved out of the answer.
     */
    kind?: 'tool' | 'summary' | 'narration';
}

/** Clamp the drawer width to a sane range so the panel stays usable. */
const MIN_WIDTH_PX = 320;
const MAX_WIDTH_PX = 720;
const DEFAULT_WIDTH_PX = 400;

/** Display mode for the Gable surface. */
export type GableMode = 'drawer' | 'expanded';

/**
 * An out-of-band change to the org's background-suggestion queue (SD-8104 / SD-9015).
 *
 * `formSubmissionIds` is populated only for `'superseded'`, where the server knows exactly which
 * requests were affected. A `'staged'` event carries a proposal id rather than a submission id, so
 * the list is empty there — treat empty as "unknown, could be any request" and refresh
 * unconditionally rather than assuming nothing matched.
 */
export interface GableSuggestionsChange {
    /**
     * `committed` is different in kind from the other two: staged/superseded change
     * only which SUGGESTIONS exist, so surfaces reading `pendingProposals$` self-heal. A commit
     * changes the RECORD — Gable posted the reply, set the status, assigned the vendor — so the
     * request's own data is now stale on every page showing it.
     */
    kind: 'staged' | 'superseded' | 'committed';
    formSubmissionIds: number[];
    /** Typed action that ran, e.g. 'reply_to_request'. Only set for `committed`. */
    actionType?: string;
    /**
     * Product modules the write touched, from the action's own declaration ('charges',
     * 'transactions', 'violation', 'amenities'). A page showing one of them is stale.
     * Only set for `committed`.
     */
    permissionModules?: string[];
    /** The record the action was about, when it had a single one. Only set for `committed`. */
    entityId?: number | null;
}

/**
 * A long-running deliverable surfaced as a first-class task in the Activity
 * tab (option B). Kicked off from chat (e.g. a ledger analysis), it runs for
 * minutes in the background — so it lives here as running → ready/failed
 * instead of a chat card that times out. Never auto-fails; only a real
 * ai.sandbox.failed sets 'failed'.
 */
export interface SandboxTask {
    runKey: string;
    conversationId: number;
    title: string;
    state: 'running' | 'ready' | 'failed';
    /** Frozen card payload once ready: build_document (deliverable card) or analyze_ledger (ledger card). */
    card?: GableToolCard['payload'];
    error?: string;
    startedAt: Date;
    /** Watchdog window elapsed without completion — still running, just "taking longer than usual". */
    slow?: boolean;
}
/** Task key prefix for a workflow run started from the Workflows panel (SD-9963). */
export const WORKFLOW_RUN_TASK_PREFIX = 'workflow-run-';

/** Workflow-run poll ladder from the newest watch: 8s for the first minute, 30s until 10 minutes, 60s until core's 60-minute run cap. */
const WORKFLOW_RUN_POLL_QUICK_MS = 8_000;
const WORKFLOW_RUN_POLL_QUICK_WINDOW_MS = 60_000;
const WORKFLOW_RUN_POLL_STEADY_MS = 30_000;
const WORKFLOW_RUN_POLL_STEADY_WINDOW_MS = 600_000;
const WORKFLOW_RUN_POLL_SLOW_MS = 60_000;
const WORKFLOW_RUN_WATCH_CAP_MS = 3_600_000;
const WORKFLOW_RUN_POLL_MAX_ERRORS = 3;
const WORKFLOW_RUN_UNREACHABLE_COPY = 'Could not check on the run. Open Workflows later for the result.';

export interface GableWorkflowRunFinished {
    runId: number;
    workflowId: number;
    run: GableWorkflowRun;
}

interface WatchedWorkflowRun {
    workflowId: number;
    title: string;
    watchedAt: number;
}

const DEFAULT_MODE: GableMode = 'drawer';

/** Cap on transaction ids seeded into one Gable message via seedTransactionApproval (SD-9233). */
const TRANSACTION_APPROVAL_ID_CAP = 500;

/**
 * API-backed drawer chrome preference (UserPreferencesApiService). One
 * object for both knobs — the backend requires object values, and width +
 * mode always travel together anyway.
 */
const GABLE_DRAWER_PREF_KEY = 'gable.drawer';

interface GableDrawerPref {
    width?: number;
    mode?: GableMode;
}

/** Conversation-list page size (SD-6742). Server caps at 50; 25 matches the default. */
const CONVERSATIONS_PER_PAGE = 25;

/** Debounce window (ms) for the history search input before hitting the server. */
const HISTORY_SEARCH_DEBOUNCE_MS = 300;

/**
 * The modules a registered Gable action can write in (SD-9808). Mirrors what core derives
 * from its action registry for the `gable-suggestions-{orgId}` channel gate; a beta user with
 * write access in any of them may subscribe. Forms alone was the old rule, and it left a
 * Transactions-only admin with no live updates.
 */
const GABLE_ACTION_MODULES: string[] = [
    RouteModules.FORMS,
    RouteModules.TRANSACTIONS,
    RouteModules.VIOLATION,
    RouteModules.CHARGES,
    RouteModules.PAYMENTS,
    RouteModules.BILLPAY,
    RouteModules.AMENITIES,
    RouteModules.CALENDAR,
    RouteModules.COMMUNICATION,
    RouteModules.MESSAGING,
    RouteModules.SURVEYS,
    RouteModules.VOTING,
];

/**
 * Safety-net watchdog: forcibly finalize stuck streams. Mirrors the page-
 * scoped watchdog Gable used; lives in the service now so it survives
 * navigation between routes.
 */
const STREAM_WATCHDOG_MS = 180_000;

/** Grace after a tab regains focus for Echo to resubscribe and deliver before we recover. */
const VISIBILITY_RECOVERY_GRACE_MS = 3_000;

/**
 * Silence that means a stream is genuinely stalled rather than merely long. A healthy
 * turn emits at least every 100ms (the box buffers deltas to that cadence), so this is
 * orders of magnitude past normal — recovery must never tear down a working stream.
 */
const STREAM_SILENCE_MS = 20_000;

/**
 * While a turn streams, a quiet channel asks core whether the turn already finished.
 * The broker can lose the completed event outright (a reconnect past the replay window, an
 * oversized payload); without this the bubble spins until
 * STREAM_WATCHDOG_MS even though the answer was saved minutes earlier.
 */
const STALL_POLL_MS = 10_000;
const GABLE_TURN_FAILED_COPY =
    'Gable couldn\'t finish this reply. Nothing was changed — please try again.';

/** Frozen contract (SD-10526 section 3): auto-resend cap for a `gable_busy` 503 before giving up. */
const GABLE_BUSY_MAX_ATTEMPTS = 3;
/** Fallback wait when core's `retryAfterSeconds` is missing or non-positive. */
const GABLE_BUSY_DEFAULT_RETRY_SECONDS = 10;
const GABLE_BUSY_FINAL_COPY = 'Gable is busy. Please try again in a minute.';
const GABLE_DAILY_LIMIT_COPY = 'You\'ve reached today\'s limit for Gable. Please try again tomorrow.';
const GABLE_RATE_LIMITED_COPY = 'Too many actions at once. Try again in a moment.';

/**
 * Per-run safety-net watchdog for async sandbox deliverable generation
 * (SD-7240). The stream watchdog above is disarmed on `ai.stream.completed`
 * and does NOT cover the sandbox-generating phase — only an `ai.sandbox.ready`
 * / `ai.sandbox.failed` broadcast clears a pending `generating` card. If the
 * Lambda dies (its message goes to the DLQ on the core side) and
 * `ai.sandbox.failed` never fires, the card would shimmer forever. This timer
 * is armed when a run becomes pending, re-armed on each `ai.sandbox.progress`
 * milestone (progress = still alive), and disarmed on the terminal event; on
 * expiry the pending card is converted to a soft-failure in place. Generations
 * run for minutes, so this is set comfortably past the longest expected run.
 */
const SANDBOX_WATCHDOG_MS = 300_000;

/** Tools whose background runs surface as Activity-tab tasks (SD-7240 documents, SD-10153 ledger). */
const SANDBOX_TASK_TOOLS: readonly string[] = ['build_document', 'analyze_ledger'];

/**
 * Singleton coordinator for the omnipresent Gable AI drawer.
 *
 * - Owns open / close state and the persisted user-resizable width.
 * - Owns the full assistant conversation (messages, streaming bubble, pending
 *   proposals, pending question card) so chat state survives route changes.
 * - Brokers all streaming/Echo subscription lifecycle so navigating mid-turn
 *   doesn't tear the channel down out from under the active stream.
 *
 * The Gable drawer host component is a thin view: it subscribes to the
 * observables here, dispatches user input via the public methods, and never
 * holds chat state of its own.
 */
@Injectable({
    providedIn: 'root',
})
export class GableDrawerService implements OnDestroy {
    // -------------------------------------------------------------------------
    // Open / close + width
    // -------------------------------------------------------------------------

    private readonly _isOpen$ = new BehaviorSubject<boolean>(false);
    readonly isOpen$: Observable<boolean> = this._isOpen$.asObservable();

    private readonly _widthPx$ = new BehaviorSubject<number>(DEFAULT_WIDTH_PX);
    readonly widthPx$: Observable<number> = this._widthPx$.asObservable();

    /**
     * Display mode: 'drawer' (right-edge slide) or 'expanded' (full-width
     * centered dialog). User-toggleable from the drawer header and
     * persisted via UserPreferencesService.
     */
    private readonly _mode$ = new BehaviorSubject<GableMode>(DEFAULT_MODE);
    readonly mode$: Observable<GableMode> = this._mode$.asObservable();

    private readonly _draftMessage$ = new BehaviorSubject<string>('');
    readonly draftMessage$: Observable<string> = this._draftMessage$.asObservable();

    /** Files staged in the composer, sent with the next message. */
    private readonly _pendingAttachments$ = new BehaviorSubject<GableChatAttachment[]>([]);
    readonly pendingAttachments$: Observable<GableChatAttachment[]> = this._pendingAttachments$.asObservable();

    addPendingAttachments(attachments: GableChatAttachment[]): void {
        const taken = new Set(this._pendingAttachments$.value.map(gableAttachmentKey));
        const fresh = attachments.filter((a) => !taken.has(gableAttachmentKey(a)));
        if (fresh.length) {
            this._pendingAttachments$.next([...this._pendingAttachments$.value, ...fresh]);
        }
    }

    removePendingAttachment(key: string): void {
        this._pendingAttachments$.next(this._pendingAttachments$.value.filter((a) => gableAttachmentKey(a) !== key));
    }

    clearPendingAttachments(): void {
        this._pendingAttachments$.next([]);
    }

    /** Fires when a programmatic seed needs the host back on the chat tab (SD-9233). */
    private readonly _chatViewRequested$ = new Subject<void>();
    readonly chatViewRequested$: Observable<void> = this._chatViewRequested$.asObservable();

    /** Fires when a caller wants the host on the Activity tab with a module filter applied (SD-9246). */
    private readonly _activityViewRequested$ = new Subject<GableActivityFilter>();
    readonly activityViewRequested$: Observable<GableActivityFilter> = this._activityViewRequested$.asObservable();

    // -------------------------------------------------------------------------
    // Chat state — exposed as observables so the host can render with OnPush.
    // -------------------------------------------------------------------------

    private readonly _messages$ = new BehaviorSubject<GableChatMessage[]>([]);
    readonly messages$: Observable<GableChatMessage[]> = this._messages$.asObservable();

    private readonly _pendingProposals$ = new BehaviorSubject<GablePendingProposal[]>([]);
    readonly pendingProposals$: Observable<GablePendingProposal[]> = this._pendingProposals$.asObservable();

    /**
     * Whether the queue has SETTLED, which `pendingProposals$` cannot answer:
     * its seed `[]` replays synchronously to every new subscriber, so a
     * subscriber that set its own flag inside the callback was marking the queue
     * loaded before the fetch had even been issued — and the Activity tab's
     * merged empty state painted over a queue that was about to arrive.
     * True after next, after error, and after the flag/audience early return,
     * which is a queue that will never load rather than one still loading.
     */
    private readonly _pendingProposalsLoaded$ = new BehaviorSubject<boolean>(false);
    readonly pendingProposalsLoaded$: Observable<boolean> = this._pendingProposalsLoaded$.asObservable();

    private readonly _suggestionsChanged$ = new Subject<GableSuggestionsChange>();
    /**
     * Fires whenever the org's suggestion queue changed out-of-band — one landed, or one was retired
     * because staff handled the request (SD-9015).
     *
     * Most surfaces do NOT need this: they already re-render off `pendingProposals$`, which this
     * service refetches on the same events. Subscribe here only when a page needs to react to the
     * change itself (e.g. reloading a request row) rather than to the proposal list.
     */
    readonly suggestionsChanged$: Observable<GableSuggestionsChange> = this._suggestionsChanged$.asObservable();

    private readonly _pendingQuestions$ = new BehaviorSubject<GablePendingQuestions | null>(null);
    readonly pendingQuestions$: Observable<GablePendingQuestions | null> = this._pendingQuestions$.asObservable();

    // Fires once each time a write action commits successfully (from the chat
    // card OR the Activity panel — both funnel through commitActionProposal
    // below). The global drawer host listens and soft-reloads the current
    // route so the page behind the drawer reflects the write. SD-8019.
    private readonly _actionCommitted$ = new Subject<void>();
    readonly actionCommitted$: Observable<void> = this._actionCommitted$.asObservable();

    private readonly _isSending$ = new BehaviorSubject<boolean>(false);
    readonly isSending$: Observable<boolean> = this._isSending$.asObservable();

    private readonly _turnInFlightNotice$ = new BehaviorSubject<boolean>(false);
    readonly turnInFlightNotice$: Observable<boolean> = this._turnInFlightNotice$.asObservable();

    /**
     * The Echo channel itself errored mid-turn (auth 403, socket drop) — NOT the turn
     * failing. The box is unaffected, so this is a calm "still working" notice, not the
     * red error state; cleared with every other turn-scoped notice once the turn settles.
     */
    private readonly _connectionLostNotice$ = new BehaviorSubject<boolean>(false);
    readonly connectionLostNotice$: Observable<boolean> = this._connectionLostNotice$.asObservable();

    /**
     * Gable-is-busy retry notice (SD-10526): seconds remaining until the same
     * `clientTurnId` auto-resends, or null when no busy retry is pending.
     */
    private readonly _busySecondsRemaining$ = new BehaviorSubject<number | null>(null);
    readonly busySecondsRemaining$: Observable<number | null> = this._busySecondsRemaining$.asObservable();

    /**
     * The box's "this is taking a while, I've moved it to the background" line for the
     * turn in flight. Turn-scoped on purpose (SD-9787): it used to live only on the message
     * row, where the stall watchdog's force-finalize flipped the row to complete and the
     * notice, the working dots and the handed-back composer all vanished mid-turn.
     */
    private readonly _longRunNotice$ = new BehaviorSubject<string | null>(null);
    readonly longRunNotice$: Observable<string | null> = this._longRunNotice$.asObservable();

    private readonly _apiSuggestions$ = new BehaviorSubject<string[]>([]);
    readonly apiSuggestions$: Observable<string[]> = this._apiSuggestions$.asObservable();

    private readonly _feedbackByMessageId = new Map<number, GableFeedback>();
    private readonly _feedback$ = new BehaviorSubject<Map<number, GableFeedback>>(this._feedbackByMessageId);
    readonly feedback$: Observable<Map<number, GableFeedback>> = this._feedback$.asObservable();

    conversationId: number | null = null;
    submittingAnswers = false;
    proposalInFlight: number | null = null;

    // -------------------------------------------------------------------------
    // Chat history (SD-6742) — list of past conversations + the title of the
    // one currently loaded. Drawer-mode title menu and expanded-mode sidebar
    // both render off these observables.
    // -------------------------------------------------------------------------

    private readonly _activeConversations$ = new BehaviorSubject<GableConversationSummary[]>([]);
    readonly activeConversations$: Observable<GableConversationSummary[]> = this._activeConversations$.asObservable();

    private readonly _archivedConversations$ = new BehaviorSubject<GableConversationSummary[]>([]);
    readonly archivedConversations$: Observable<GableConversationSummary[]> = this._archivedConversations$.asObservable();

    /** Title of the conversation currently loaded (null for a fresh/unnamed chat). */
    private readonly _activeTitle$ = new BehaviorSubject<string | null>(null);
    readonly activeTitle$: Observable<string | null> = this._activeTitle$.asObservable();

    /** True while a history list refresh or a load is in flight (spinner hook). */
    private readonly _historyLoading$ = new BehaviorSubject<boolean>(false);
    readonly historyLoading$: Observable<boolean> = this._historyLoading$.asObservable();

    /**
     * True while a paged active-list fetch (search reset OR load-more append)
     * is in flight. Separate from `historyLoading$` — that one also gates the
     * conversation-OPEN skeleton, so reusing it here would flash the skeleton
     * over the chat body on every keystroke. This drives a small inline spinner
     * in the sidebar list footer only.
     */
    private readonly _conversationsLoading$ = new BehaviorSubject<boolean>(false);
    readonly conversationsLoading$: Observable<boolean> = this._conversationsLoading$.asObservable();

    /** Whether the server reports another page of active conversations exists. */
    private readonly _hasMoreConversations$ = new BehaviorSubject<boolean>(false);
    readonly hasMoreConversations$: Observable<boolean> = this._hasMoreConversations$.asObservable();

    /**
     * Archived-list paging mirrors the active list, but with its OWN term and
     * page cursor: the Archived surface is a full panel with its own search
     * box, so the rail's search must not reach into it (and vice versa).
     */
    private readonly _archivedLoading$ = new BehaviorSubject<boolean>(false);
    readonly archivedLoading$: Observable<boolean> = this._archivedLoading$.asObservable();

    private readonly _hasMoreArchived$ = new BehaviorSubject<boolean>(false);
    readonly hasMoreArchived$: Observable<boolean> = this._hasMoreArchived$.asObservable();

    /**
     * Archived-list load failures, for the host to growl. The service has no
     * GrowlerService of its own (and `_fetchArchivedPage` has no component call
     * site to growl at), so the error is published and the host owns the toast.
     * Without this a 5xx is indistinguishable from an empty archive — the panel
     * renders "No archived chats yet", which asserts the opposite of the truth.
     */
    private readonly _archivedError$ = new Subject<HttpErrorResponse>();
    readonly archivedError$: Observable<HttpErrorResponse> = this._archivedError$.asObservable();

    /**
     * Fired AFTER an org/scope reset has cleared the archived state. The panel's
     * filter/sort/term are component-owned, so the service cannot clear them
     * itself; this is how it tells the host to. Emitted post-reset on purpose —
     * a host that refetched first would populate rows the reset then wipes.
     * Covers BOTH triggers (`orgContextChanged$` and `scopeKey$`), which is why
     * the host listens here rather than to either one directly.
     */
    private readonly _archivedReset$ = new Subject<void>();
    readonly archivedReset$: Observable<void> = this._archivedReset$.asObservable();

    /** Server-side search term applied to the archived list only. */
    private _archivedSearch = '';

    /**
     * Server-side ordering for the archived list. Lives here rather than in the
     * panel because it is a request parameter: the list is paged, so ordering the
     * rows the panel happens to hold orders a window, not the archive.
     */
    private _archivedSort: GableConversationSort = 'recent';

    /**
     * Server-side tenant filter for the archived list; `null` is "all scopes".
     * Also a request parameter, and for a second reason: filtering client-side
     * can leave the panel too short to scroll, and scrolling is the only thing
     * that fetches the next page.
     */
    private _archivedScopeFilter: GableConversationScopeRef | null = null;

    /** Highest page of the archived list currently accumulated (1-based). */
    private _archivedPage = 1;

    /**
     * The tenants the archived list can be filtered by — server-owned, because a
     * filtered response only ever contains one scope and so cannot describe the
     * menu it was chosen from.
     */
    private readonly _archivedScopes$ = new BehaviorSubject<GableConversationScope[]>([]);
    readonly archivedScopes$: Observable<GableConversationScope[]> = this._archivedScopes$.asObservable();

    /** Current archived ordering — the panel renders its label from this. */
    get archivedSort(): GableConversationSort {
        return this._archivedSort;
    }

    /** Current archived tenant filter, or `null` for all scopes. */
    get archivedScopeFilter(): GableConversationScopeRef | null {
        return this._archivedScopeFilter;
    }

    /**
     * Current server-side search term applied to the active list. Empty string
     * means "no filter" (normal first page). Mirrored to the archived fetch so
     * both lists honor the same term.
     */
    private _conversationSearch = '';

    /** Highest page of the active list currently accumulated (1-based). */
    private _conversationPage = 1;

    get conversationSearch(): string {
        return this._conversationSearch;
    }

    /**
     * Debounced search pipeline (SD-6742). Component pushes raw keystrokes into
     * `_searchInput$`; this maps each (after a ~300ms quiet window) to a fresh
     * page-1 fetch via `switchMap` so an in-flight request for a stale term is
     * cancelled when the user keeps typing.
     */
    private readonly _searchInput$ = new Subject<string>();

    /** Same pipeline, independent term, for the Archived chats panel. */
    private readonly _archivedSearchInput$ = new Subject<string>();

    private _streamHandle: AiConversationStreamHandle | null = null;
    private _streamSub: Subscription | null = null;

    /**
     * Stop-on-401 latch (SD-7898). Set when a conversations/proposals refresh
     * gets a 401 (the request carried a token the server has blacklisted — e.g.
     * mid org-switch). While set, the refresh entrypoints no-op instead of
     * re-firing the same doomed request (which would keep logging
     * `token_blacklisted` audit rows). Cleared on an org-context change, which
     * establishes a fresh context to load under.
     */
    private _refreshHalted = false;

    /** Currently-subscribed background-suggestions channel, or null (SD-8104). */
    private _suggestionsChannel: string | null = null;
    /**
     * Message id of the assistant bubble currently receiving deltas.
     * Captured at stream-start so `cancelStream()` can POST a cancel
     * signal scoped to that message — independent of whatever's
     * happening in the UI when the user clicks Stop.
     */
    private _streamingAssistantMessageId: number | null = null;
    private _streamWatchdog: ReturnType<typeof setTimeout> | null = null;
    private _stallPoll: ReturnType<typeof setInterval> | null = null;
    private _stallCheckInFlight = false;

    /** gable_busy (503) auto-retry state (SD-10526) — see `_armBusyRetry` / `_clearBusyRetry`. */
    private _busyRetryAttempt = 0;
    private _busyRetryTimeout: ReturnType<typeof setTimeout> | null = null;
    private _busyCountdownInterval: ReturnType<typeof setInterval> | null = null;

    /**
     * The `visibilitychange` listener and the grace timer it arms, kept as fields
     * so `ngOnDestroy` can detach them. An inline arrow could never be removed,
     * and `document` outlives every instance: a service that is thrown away with
     * the listener still attached keeps reacting to tab visibility forever.
     */
    private readonly _onVisibilityChange = (): void => {
        if (document.visibilityState !== 'visible') {
            this._clearWorkflowRunPollTimer();
            return;
        }
        this._resumeWorkflowRunPoll();
        if (!this._isSending$.value) {
            return;
        }
        this._clearVisibilityRecoveryTimer();
        this._visibilityRecoveryTimer = setTimeout(() => {
            this._visibilityRecoveryTimer = null;
            // Only a SILENT stream is stuck. A long answer still streaming is
            // healthy, and finalizing it would produce the empty bubble this
            // recovery exists to cure.
            if (this._isSending$.value && this._streamHasGoneSilent()) {
                this._forceFinalizeStuckStream();
            }
        }, VISIBILITY_RECOVERY_GRACE_MS);
    };
    private _visibilityRecoveryTimer: ReturnType<typeof setTimeout> | null = null;

    /** Constructor-wired subscriptions, dropped together in `ngOnDestroy`. */
    private readonly _lifetimeSubs = new Subscription();

    /** When the current stream last produced anything; drives the stalled-vs-slow check. */
    private _lastStreamEventAt = 0;

    /**
     * True between sending the first message of a brand-new conversation and
     * receiving its generated title (SD-6742). The title comes from a
     * post-completion Haiku job that can land ~20–30s after `ai.stream.completed`
     * — far slower than the follow-ups job — so while this is set we hold the
     * Echo channel open longer than the usual window so the
     * `ai.conversation.titled` broadcast updates the header + recent list live.
     */
    private _awaitingFirstTitle = false;

    /**
     * Conversation id → pending sandbox run keys for in-flight sandbox
     * deliverable generations (SD-7240). A sandbox `build_document` finishes
     * asynchronously — minutes after `ai.stream.completed` — on the SAME Echo
     * channel, and multiple runs can be in flight per conversation. While a
     * conversation has any pending run key we DEFER the channel teardown so
     * the `ai.sandbox.ready` / `ai.sandbox.failed` events can still land.
     * Each run key is removed when its ready/failed arrives; the conversation
     * entry is deleted (and teardown allowed) once its set empties.
     */
    private readonly _pendingSandboxConversationIds = new Map<number, Set<string>>();

    /** Live view of a still-streaming turn, stashed per conversation on switch-away so thoughts survive a round trip. */
    private readonly _liveTurnCache = new Map<number, GableChatMessage[]>();

    /** Tenant of the open conversation when it came from another scope — sends continue there. */
    private _activeConversationScope: GableConversationScope | null = null;

    /**
     * Per-run sandbox watchdog timers, keyed by the same run key used for the
     * pending card (`runId` or the legacy `sandbox-{conversationId}` fallback).
     * Armed/re-armed on progress, disarmed on the terminal ready/failed event,
     * and cleared wholesale on teardown / new conversation so timers never leak
     * past the singleton's active lifetime. See `SANDBOX_WATCHDOG_MS`.
     */
    private readonly _sandboxWatchdogs = new Map<string, ReturnType<typeof setTimeout>>();

    /**
     * Long-running deliverable tasks, surfaced in the Activity tab (option B)
     * instead of as timing-out chat cards. running → ready/failed.
     */
    private readonly _sandboxTasks$ = new BehaviorSubject<SandboxTask[]>([]);
    readonly sandboxTasks$ = this._sandboxTasks$.asObservable();

    /**
     * Whether the task list has settled. Starts TRUE — tasks accumulate in memory
     * across conversations and the only asynchronous source is
     * `_rehydrateSandboxTasksFromHistory`, so the list is settled except while a
     * conversation's history is in flight.
     */
    private readonly _sandboxTasksLoaded$ = new BehaviorSubject<boolean>(true);
    readonly sandboxTasksLoaded$: Observable<boolean> = this._sandboxTasksLoaded$.asObservable();

    /** Workflow runs still being polled for, keyed by run id; lives here so it outlives the Workflows panel. */
    private readonly _watchedWorkflowRuns = new Map<number, WatchedWorkflowRun>();
    private _workflowRunPoll: ReturnType<typeof setTimeout> | null = null;
    private _workflowRunPollInFlight = false;
    private _workflowRunPollErrors = 0;

    private readonly _runningWorkflowRunIds$ = new BehaviorSubject<ReadonlySet<number>>(new Set());
    readonly runningWorkflowRunIds$: Observable<ReadonlySet<number>> = this._runningWorkflowRunIds$.asObservable();

    private readonly _workflowRunFinished$ = new Subject<GableWorkflowRunFinished>();
    readonly workflowRunFinished$: Observable<GableWorkflowRunFinished> = this._workflowRunFinished$.asObservable();

    constructor(
        private _service: GableService,
        private _streamService: GableStreamService,
        private _prefs: UserPreferencesService,
        private _prefsApi: UserPreferencesApiService,
        private _orgContext: OrgContextService,
        private _echo: EchoService,
        private _scope: GableScopeService,
        private _errorReporter: GableClientErrorReporterService,
        private _workflows: GableWorkflowsService,
        private _growler: GrowlerService,
    ) {
        // Pause + reset when the viewed org changes (SD-7898). The drawer host
        // mounts at app root and survives the org-switch rebuild, so without this
        // it keeps firing conversations/proposals against the previous org's
        // now-blacklisted token — the `token_blacklisted` storm. OrgContextService
        // fires AFTER the new token is stored, so the re-hydrate runs under the new
        // org. A root singleton in the app, but `ngOnDestroy` still detaches every
        // externally-held subscription and timer — a TestBed-created instance is
        // otherwise kept alive by the providers it subscribed to.
        this._lifetimeSubs.add(
            this._orgContext.orgContextChanged$.subscribe(() => this._resetForOrgContextChange()),
        );

        // Same reset when the org↔PMC scope boundary or the viewed PMC changes (SD-9208).
        this._lifetimeSubs.add(
            this._scope.scopeKey$.pipe(skip(1)).subscribe(() => this._resetForOrgContextChange()),
        );

        // The still-working notices never outlive the turn they describe.
        this._lifetimeSubs.add(this._isSending$.subscribe((s) => {
            if (!s) {
                this._turnInFlightNotice$.next(false);
                this._longRunNotice$.next(null);
                this._connectionLostNotice$.next(false);
            }
        }));

        // A hidden tab gives up its realtime socket after 5 min (SD-7778), so a turn
        // that finishes while you are in another window delivers nothing and the
        // bubble spins until the 90s watchdog. Coming back is the strongest signal
        // that someone is waiting on it: let Echo resubscribe, then recover.
        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', this._onVisibilityChange);
        }

        // Hydrate width + mode. Source of truth is the API-backed prefs
        // service (per-user, per-org, follows the user across devices); the
        // legacy localStorage values only serve as the migration fallback
        // for the first paint before/without an API value.
        const legacyWidth = this._prefs.getPreference<number>(
            USER_PREFERENCE_KEYS.GABLE_DRAWER_WIDTH,
            DEFAULT_WIDTH_PX,
        );
        const legacyMode = this._prefs.getPreference<GableMode>(
            USER_PREFERENCE_KEYS.GABLE_DRAWER_MODE,
            DEFAULT_MODE,
        );
        const apiPref = this._prefsApi.get<GableDrawerPref>(GABLE_DRAWER_PREF_KEY);
        this._widthPx$.next(this._clampWidth(apiPref?.width ?? legacyWidth));
        const initialMode = apiPref?.mode ?? legacyMode;
        this._mode$.next(initialMode === 'expanded' ? 'expanded' : 'drawer');

        // Apply a late-arriving API value (fresh device: loadAll resolves
        // after this constructor). Our own writes echo through here as
        // no-ops thanks to the same-value guards in setWidth/setMode.
        this._lifetimeSubs.add(this._prefsApi.watch<GableDrawerPref>(GABLE_DRAWER_PREF_KEY).subscribe((pref) => {
            if (!pref) {
                return;
            }
            if (typeof pref.width === 'number') {
                const clamped = this._clampWidth(pref.width);
                if (clamped !== this._widthPx$.value) {
                    this._widthPx$.next(clamped);
                }
            }
            if (!this._modeChosenThisSession && (pref.mode === 'drawer' || pref.mode === 'expanded') && pref.mode !== this._mode$.value) {
                this._mode$.next(pref.mode);
            }
        }));

        // Debounced server-side search. switchMap cancels the HTTP for a stale
        // term as soon as the user types again. The source is this service's own
        // Subject, so the subscription lives exactly as long as the instance
        // (matching the BehaviorSubjects above).
        this._searchInput$
            .pipe(
                debounceTime(HISTORY_SEARCH_DEBOUNCE_MS),
                switchMap((term) => {
                    this._conversationSearch = term.trim();
                    this._conversationPage = 1;
                    this._conversationsLoading$.next(true);
                    // Archived does NOT ride this term any more — it is its own
                    // panel with its own search box (see _archivedSearchInput$).
                    // Capture the org this search is issued under. switchMap cancels a
                    // superseded *term*, but an org switch mid-search does not emit a new
                    // term — so a late prior-org result must be discarded, else it would
                    // replace the new org's freshly-loaded list with the prior org's
                    // conversations (cross-org leak, SD-7898).
                    const issuedForScope = this._currentScopeStamp();
                    return this._service.listConversations({
                        status: 'active',
                        allScopes: true,
                        search: this._conversationSearch,
                        page: 1,
                        perPage: CONVERSATIONS_PER_PAGE,
                    }).pipe(
                        map((res) => ({ res, issuedForScope })),
                        // Handle the error INSIDE switchMap so it never reaches
                        // the outer subscriber and tears down this app-lifetime
                        // subscription — otherwise one transient failure would
                        // silently disable history search for the whole session.
                        // Render an empty result; the next keystroke retries — but only
                        // for the current org (a stale-context failure must not wipe the
                        // new org's list).
                        catchError(() => {
                            if (!this._isStaleGableResponse(issuedForScope)) {
                                this._activeConversations$.next([]);
                                this._hasMoreConversations$.next(false);
                                this._conversationsLoading$.next(false);
                            }
                            return EMPTY;
                        }),
                    );
                }),
            )
            .subscribe({
                next: ({ res, issuedForScope }) => {
                    if (this._isStaleGableResponse(issuedForScope)) {
                        return;
                    }
                    this._applyActiveListPage(res, false);
                },
            });

        // Archived panel search — same debounce/switchMap shape, own term. It
        // deliberately does NOT share `_searchInput$`: the two surfaces are
        // visible at the same time and each owns its own result set.
        this._archivedSearchInput$
            .pipe(
                debounceTime(HISTORY_SEARCH_DEBOUNCE_MS),
                switchMap((term) => {
                    this._archivedSearch = term.trim();
                    this._archivedPage = 1;
                    this._archivedLoading$.next(true);
                    const issuedForScope = this._currentScopeStamp();
                    return this._service.listConversations({
                        status: 'archived',
                        allScopes: true,
                        search: this._archivedSearch,
                        page: 1,
                        perPage: CONVERSATIONS_PER_PAGE,
                        sort: this._archivedSort,
                        scope: this._archivedScopeFilter ?? undefined,
                    }).pipe(
                        map((res) => ({ res, issuedForScope })),
                        // Swallow inside switchMap so a transient failure can't tear
                        // down this app-lifetime subscription (see the active pipeline).
                        catchError((err: HttpErrorResponse) => {
                            if (!this._isStaleGableResponse(issuedForScope)) {
                                // Publish before clearing: a failed search must not
                                // read as a zero-result search.
                                this._archivedError$.next(err);
                                this._archivedConversations$.next([]);
                                this._hasMoreArchived$.next(false);
                                this._archivedLoading$.next(false);
                            }
                            return EMPTY;
                        }),
                    );
                }),
            )
            .subscribe({
                next: ({ res, issuedForScope }) => {
                    if (this._isStaleGableResponse(issuedForScope)) {
                        // Stale, but this fetch is over — clear the flag or the
                        // panel spins forever (see _fetchArchivedPage).
                        this._archivedLoading$.next(false);
                        return;
                    }
                    this._applyArchivedListPage(res, false);
                },
            });
    }

    // -------------------------------------------------------------------------
    // Feature flag / open-close
    // -------------------------------------------------------------------------

    /** Synchronous feature-flag check. Backed by JWT claims. */
    get flagEnabled(): boolean {
        return LegFiJwtService.featureEnabled('ai-gable.enabled');
    }

    /** History-fetch flag gate: PMC scope bypasses the JWT-org flag — core checks the PMC's own flags (SD-9208). */
    private get _historyFlagOk(): boolean {
        return this.flagEnabled || this._scope.isPmcScoped;
    }

    /** Whether the current user may see Gable at all: `gablePmc` in PMC scope, else the org-scoped `gable` claim (SD-10198). */
    get canUseGable(): boolean {
        const jwt = LegFiJwtService.read();
        return this._scope.isPmcScoped ? jwt?.gablePmc === true : jwt?.gable === true;
    }

    /**
     * The ONE predicate every Gable/AI entry point should gate on: the org has the feature
     * AND this user is allowed to see it.
     *
     * `flagEnabled` alone is not sufficient. The org flag is on its way to being globally
     * enabled, at which point a flag-only check would expose Gable to every admin in every
     * org — so surfaces must ask this, which also carries the super/beta identity check.
     */
    get canSeeGable(): boolean {
        return this.flagEnabled && this.canUseGable;
    }

    /**
     * Whether the current user is in Gable's review audience — deliberately NARROWER than
     * {@link canUseGable}.
     *
     * This gates the eager background work (the `gable-suggestions-{orgId}` channel subscribe plus
     * the pending-proposals / conversations fetch), and that channel's authorizer requires
     * Forms-write (routes/channels.php), NOT super. Subscribe-rights must match see-rights (SD-8121's
     * stated invariant): a user who subscribes without the permission 403s /broadcasting/auth on
     * every page load, which was ~44% of prod denials (SD-8321).
     *
     * Supers short-circuit server-side; deliberately NOT routed through {@link canSeeGable}/{@link canUseGable} (SD-10198), which carry no such permission check.
     */
    private get _inReviewAudience(): boolean {
        const jwt = LegFiJwtService.read();
        if (jwt?.superUser === true) {
            return true;
        }

        return jwt?.gable === true
            && GABLE_ACTION_MODULES.some((m) => LegFiJwtService.doesUserHaveModulePermission(m, true));
    }

    // A locked-out (orgExpired) admin sits on /app/lock — every drawer entry point refuses, not just the hidden pill.
    private get _lockedOut(): boolean {
        return LegFiJwtService.read()?.orgExpired === true;
    }

    open(): void {
        if (this._lockedOut) {
            return;
        }
        if (!this._isOpen$.value) {
            this._isOpen$.next(true);
        }
    }

    close(): void {
        if (this._isOpen$.value) {
            this._isOpen$.next(false);
        }
    }

    toggle(): void {
        if (this._lockedOut) {
            return;
        }
        this._isOpen$.next(!this._isOpen$.value);
    }

    /** Bring the chat panel forward, whatever tab the drawer was last left on. */
    showChat(): void {
        this._chatViewRequested$.next();
    }

    /** Open the drawer and prefill the composer with the given prompt. */
    seed(prompt: string): void {
        this._draftMessage$.next(prompt ?? '');
        this.open();
    }

    /** Open the drawer on a fresh chat and send a deterministic bulk-approval prompt for the given transaction ids (SD-9233). */
    seedTransactionApproval(transactionIds: number[]): void {
        const capped = transactionIds.slice(0, TRANSACTION_APPROVAL_ID_CAP);
        const remaining = transactionIds.length - capped.length;
        let prompt = `Review these ${capped.length} bank transactions and propose approving/categorizing the ones you're confident about: ids [${capped.join(', ')}]. Use the pending AI suggestions where available.`;
        if (remaining > 0) {
            prompt += ` …and ${remaining} more remain — handle in batches of ${TRANSACTION_APPROVAL_ID_CAP}.`;
        }
        this.open();
        this._chatViewRequested$.next();
        this.startNewChatWithMessage(prompt);
    }

    /** Open the drawer straight to the Activity tab, filtered to one module (SD-9246). */
    openActivity(filter: GableActivityFilter = 'payments'): void {
        this.open();
        this._activityViewRequested$.next(filter);
    }

    setDraft(text: string): void {
        this._draftMessage$.next(text);
    }

    // -------------------------------------------------------------------------
    // Width (clamped + persisted)
    // -------------------------------------------------------------------------

    setWidth(px: number): void {
        const clamped = this._clampWidth(px);
        if (clamped === this._widthPx$.value) {
            return;
        }
        this._widthPx$.next(clamped);
        // Debounced — resize drags fire this continuously.
        this._prefsApi.setDebounced<GableDrawerPref>(GABLE_DRAWER_PREF_KEY, { width: clamped, mode: this.mode });
    }

    get widthPx(): number {
        return this._widthPx$.value;
    }

    private _clampWidth(px: number): number {
        return Math.min(MAX_WIDTH_PX, Math.max(MIN_WIDTH_PX, Math.round(px)));
    }

    // -------------------------------------------------------------------------
    // Display mode (drawer | expanded dialog)
    // -------------------------------------------------------------------------

    get mode(): GableMode {
        return this._mode$.value;
    }

    /** Once the user picks a mode, a late or echoed preference never overrides it. */
    private _modeChosenThisSession = false;

    setMode(mode: GableMode): void {
        if (mode === this._mode$.value) {
            return;
        }
        this._modeChosenThisSession = true;
        this._mode$.next(mode);
        this._prefsApi.set<GableDrawerPref>(GABLE_DRAWER_PREF_KEY, { width: this.widthPx, mode });
    }

    /**
     * Flip between drawer + expanded modes. Bound to the dock-toggle button
     * in the drawer header so a single click pops out into the full-width
     * dialog (and back).
     */
    toggleMode(): void {
        this.setMode(this._mode$.value === 'drawer' ? 'expanded' : 'drawer');
    }

    // -------------------------------------------------------------------------
    // Conversation lifecycle
    // -------------------------------------------------------------------------

    /** Wipe the conversation back to a fresh empty state. */
    startNewConversation(): void {
        this._stashLiveTurn();
        this._activeConversationScope = null;
        this._teardownStream();
        this._pendingSandboxConversationIds.clear();
        this.conversationId = null;
        this._activeTitle$.next(null);
        this._messages$.next([]);
        this._pendingQuestions$.next(null);
        this._apiSuggestions$.next([]);
        this._isSending$.next(false);
        this._turnInFlightNotice$.next(false);
        this._draftMessage$.next('');
        this._pendingAttachments$.next([]);
        this._feedbackByMessageId.clear();
        this._feedback$.next(new Map(this._feedbackByMessageId));
        // A busy retry ladder belongs to the conversation that spawned it — a fresh chat
        // must not auto-resend into a conversation the user has since navigated away from.
        this._clearBusyRetry();
        this._busyRetryAttempt = 0;
        this.submittingAnswers = false;
    }

    /** Fresh conversation with the first turn already in flight (edit-with-Gable lane). */
    startNewChatWithMessage(message: string): void {
        this.startNewConversation();
        this.sendMessage(message);
    }

    // -------------------------------------------------------------------------
    // Chat history (SD-6742)
    // -------------------------------------------------------------------------

    get activeTitle(): string | null {
        return this._activeTitle$.value;
    }

    /**
     * Re-fetch page 1 of both lists with the CURRENT search term. Called when
     * the history menu / sidebar opens and after a rename/archive/title event
     * so the lists stay current without losing the active filter. Resets
     * pagination back to the first page. Non-fatal on error — an empty list
     * just renders the empty-state affordance.
     */
    refreshConversations(retitleOpenChat = false): void {
        // Gable isn't enabled for this org (e.g. after switching to an org
        // without the flag) — skip the fetch so it doesn't 403 (SD-7937). Also
        // skip for a user outside the review audience (SD-8321) — the drawer is
        // super-only-visible, so this fetch is pure waste for them.
        // In PMC scope the JWT-org flag doesn't apply — the server authorizes on the PMC's own flags (SD-9208).
        if (!this._historyFlagOk || !this._inReviewAudience || this._refreshHalted) {
            return;
        }
        const issuedForScope = this._currentScopeStamp();
        this._conversationPage = 1;
        this._conversationsLoading$.next(true);
        this._service.listConversations({
            status: 'active',
            allScopes: true,
            search: this._conversationSearch,
            page: 1,
            perPage: CONVERSATIONS_PER_PAGE,
        }).subscribe({
            next: (res) => {
                if (this._isStaleGableResponse(issuedForScope)) {
                    return;
                }
                this._applyActiveListPage(res, false);
                // Backfill the open chat's header if its titled broadcast was missed, or re-read it after Gable renamed the chat.
                if ((this._activeTitle$.value === null || retitleOpenChat) && this.conversationId !== null) {
                    const mine = res.conversations?.find((c) => c.id === this.conversationId);
                    if (mine?.title && mine.title !== this._activeTitle$.value) {
                        this._activeTitle$.next(mine.title);
                    }
                }
            },
            error: (err) => {
                if (this._isStaleGableResponse(issuedForScope)) {
                    return;
                }
                this._haltRefreshIfUnauthorized(err);
                this._activeConversations$.next([]);
                this._hasMoreConversations$.next(false);
                this._conversationsLoading$.next(false);
            },
        });
        // Archived is NOT fetched here — it's lazy (loadArchived()), called only
        // when the archived menu opens / sidebar section expands. This keeps a
        // drawer open to a SINGLE GET /conversations (active list) instead of
        // two every time (SD — "no 2 GETs on open").
    }

    /**
     * Fetch the archived list (page 1) on demand — honoring the current search
     * term. Called when the user opens the archived menu or expands the archived
     * sidebar section, so a plain drawer-open stays ONE GET /conversations
     * ("no 2 GETs on open"). `refreshConversations()` deliberately does not call
     * this; without a public entry point the archived section never populates.
     */
    loadArchived(): void {
        this._refreshArchived();
    }

    /**
     * Debounced server-side search over the ARCHIVED list only. Independent of
     * `searchConversations()` so typing in the rail doesn't rewrite the panel's
     * result set and typing in the panel doesn't rewrite the rail's.
     */
    searchArchived(term: string): void {
        this._archivedSearchInput$.next(term ?? '');
        this._archivedSearch = (term ?? '').trim();
    }

    /**
     * Re-order the archived list. Undebounced — this is a menu click, not typing —
     * and always a page-1 refetch: the order decides which rows page 1 even
     * contains, so the accumulated pages cannot be reused.
     */
    setArchivedSort(sort: GableConversationSort): void {
        if (this._archivedSort === sort) {
            return;
        }
        this._archivedSort = sort;
        this._refreshArchived();
    }

    /**
     * Narrow the archived list to one tenant, or `null` for all of them. Same
     * shape as {@link setArchivedSort}. Compared by value so re-picking the
     * current scope is a no-op rather than a refetch — and so the object identity
     * a fetch stamped itself with stays stable while the filter is unchanged.
     */
    setArchivedScopeFilter(scope: GableConversationScopeRef | null): void {
        const current = this._archivedScopeFilter;
        if (current?.kind === scope?.kind && current?.id === scope?.id) {
            return;
        }
        this._archivedScopeFilter = scope;
        this._refreshArchived();
    }

    /**
     * Append the next page of archived conversations. No-op while a page is in
     * flight or when the server says there is nothing more.
     */
    loadMoreArchived(): void {
        if (this._archivedLoading$.value || !this._hasMoreArchived$.value) {
            return;
        }
        this._fetchArchivedPage(this._archivedPage + 1, true);
    }

    /** Re-fetch the archived list (page 1) honoring the archived search term. */
    private _refreshArchived(): void {
        this._archivedPage = 1;
        this._fetchArchivedPage(1, false);
    }

    /**
     * One page of the archived list. `append` distinguishes infinite-scroll
     * (add after the existing rows) from a refresh/search reset (replace).
     */
    private _fetchArchivedPage(page: number, append: boolean): void {
        // Mirror refreshConversations()'s gates (SD-7937 flag + SD-8321 audience).
        if (!this._historyFlagOk || !this._inReviewAudience || this._refreshHalted) {
            return;
        }
        // An org switch mid-flight must not append the PRIOR org's rows onto the
        // new org's list (cross-org leak, SD-7898) — same guard as the active list.
        const issuedForScope = this._currentScopeStamp();
        // A load-more is a raw subscribe, so switchMap cannot cancel it when the
        // term changes underneath it. Stamp the TERM too: without this, page N of
        // term A lands after term B's page 1 and appends non-matching rows below
        // the search results, then leaves `_archivedPage` at N so pages 2..N of
        // term B are silently skipped.
        const issuedForTerm = this._archivedSearch;
        // Sort and scope are request parameters now, so they stale a response the
        // same way the term does: a page issued under "Recent" landing after the
        // user picked "Oldest" would append rows in the wrong order and move the
        // page cursor past rows the new order has not shown yet.
        const issuedForSort = this._archivedSort;
        const issuedForScopeFilter = this._archivedScopeFilter;
        // Every exit below clears the flag. A stale response still ENDS this
        // fetch — returning without clearing latches `archivedLoading` true, which
        // pins a permanent spinner, suppresses the empty state, and deadlocks
        // `loadMoreArchived()` on its own guard.
        const isStale = () =>
            this._isStaleGableResponse(issuedForScope)
            || this._archivedSearch !== issuedForTerm
            || this._archivedSort !== issuedForSort
            || this._archivedScopeFilter !== issuedForScopeFilter;
        this._archivedLoading$.next(true);
        this._service.listConversations({
            status: 'archived',
            allScopes: true,
            search: issuedForTerm,
            page,
            perPage: CONVERSATIONS_PER_PAGE,
            sort: issuedForSort,
            scope: issuedForScopeFilter ?? undefined,
        }).subscribe({
            next: (res) => {
                if (isStale()) {
                    this._archivedLoading$.next(false);
                    return;
                }
                this._archivedPage = res.pagination?.page ?? page;
                this._applyArchivedListPage(res, append);
            },
            error: (err: HttpErrorResponse) => {
                if (isStale()) {
                    this._archivedLoading$.next(false);
                    return;
                }
                this._haltRefreshIfUnauthorized(err);
                // Surface it: without this a 5xx renders as "No archived chats
                // yet", which tells the user the opposite of what happened.
                this._archivedError$.next(err);
                // Keep an accumulated list on a failed load-more; only a page-1
                // failure clears, so the panel falls back to its empty state.
                if (!append) {
                    this._archivedConversations$.next([]);
                    this._hasMoreArchived$.next(false);
                }
                this._archivedLoading$.next(false);
            },
        });
    }

    /** Fold one archived page into state, de-duping by id on append. */
    private _applyArchivedListPage(
        res: GableConversationListResponse,
        append: boolean,
    ): void {
        const incoming = res.conversations ?? [];
        if (append) {
            const seen = new Set(this._archivedConversations$.value.map((c) => c.id));
            this._archivedConversations$.next([
                ...this._archivedConversations$.value,
                ...incoming.filter((c) => !seen.has(c.id)),
            ]);
        } else {
            this._archivedConversations$.next(incoming);
        }
        this._hasMoreArchived$.next(res.pagination?.hasMore ?? false);
        // Only page 1 of an unfiltered listing describes the whole menu; later
        // pages repeat it, and an older server omits it entirely — in both cases
        // keeping what we have beats blanking the filter the user is inside of.
        if (res.scopes !== undefined) {
            this._archivedScopes$.next(res.scopes);
        }
        this._archivedLoading$.next(false);
    }

    /**
     * Run a server-side search over the ACTIVE list — the expanded rail's search
     * box. Debounced ~300ms via an internal Subject + switchMap (cancels
     * in-flight searches), resets to page 1. An empty term returns the normal
     * first page. Archived is untouched: it is its own panel with its own box
     * (`searchArchived()`), and the two are on screen at the same time.
     */
    searchConversations(term: string): void {
        this._searchInput$.next(term ?? '');
        // Keep _conversationSearch current so an immediate loadMore pages within
        // the right term; the fetch itself rides the debounced pipeline (see the
        // constructor's _searchInput$ subscription).
        this._conversationSearch = (term ?? '').trim();
    }

    /**
     * Append the next page of active conversations when the server says more
     * exist. No-op while a page is already loading or when there's nothing
     * more. Uses the current search term so paging stays within the filtered
     * result set.
     */
    loadMoreConversations(): void {
        if (this._conversationsLoading$.value || !this._hasMoreConversations$.value) {
            return;
        }
        // Capture the org this page is issued under so a load-more in flight at an
        // org switch can't append the PRIOR org's conversations onto the new org's
        // freshly-reloaded list (cross-org leak, SD-7898).
        const issuedForScope = this._currentScopeStamp();
        const nextPage = this._conversationPage + 1;
        this._conversationsLoading$.next(true);
        this._service.listConversations({
            status: 'active',
            allScopes: true,
            search: this._conversationSearch,
            page: nextPage,
            perPage: CONVERSATIONS_PER_PAGE,
        }).subscribe({
            next: (res) => {
                if (this._isStaleGableResponse(issuedForScope)) {
                    return;
                }
                this._conversationPage = res.pagination?.page ?? nextPage;
                this._applyActiveListPage(res, true);
            },
            error: () => {
                // Ignore a stale prior-org failure: the org already switched and its
                // refresh owns the loading flag now — flipping it here would clear the
                // new org's in-flight page-1 spinner and let a fast scroll re-fire page 2.
                if (this._isStaleGableResponse(issuedForScope)) {
                    return;
                }
                // Leave the accumulated list intact; just stop the spinner so
                // the user can retry by scrolling again.
                this._conversationsLoading$.next(false);
            },
        });
    }

    /**
     * Fold one list-endpoint page into the active-conversations state. When
     * `append` is true the rows are added after the existing ones (load-more);
     * otherwise they replace the list (search reset / page-1 refresh). De-dupes
     * by id so an overlapping page (rows shifting between requests) can't
     * produce duplicate rows / trackBy collisions.
     */
    private _applyActiveListPage(
        res: GableConversationListResponse,
        append: boolean,
    ): void {
        const incoming = res.conversations ?? [];
        if (append) {
            const seen = new Set(this._activeConversations$.value.map((c) => c.id));
            const additions = incoming.filter((c) => !seen.has(c.id));
            this._activeConversations$.next([...this._activeConversations$.value, ...additions]);
        } else {
            this._activeConversations$.next(incoming);
        }
        this._hasMoreConversations$.next(res.pagination?.hasMore ?? false);
        this._conversationsLoading$.next(false);
    }

    /**
     * Load a past conversation and rehydrate the drawer so the user can read
     * it AND keep chatting (full resume). Tears down any in-flight stream,
     * maps the persisted message shape into `GableChatMessage`, sets
     * `conversationId` (so the next send continues this thread via
     * chat-stream), records the title, and switches the drawer to the chat
     * view.
     */
    loadConversation(conversationId: number, scope?: GableConversationScope): void {
        // Switching away mid-turn is safe — the box drives the turn server-side.
        this._stashLiveTurn();
        this._historyLoading$.next(true);
        this._teardownStream();
        this._pendingSandboxConversationIds.clear();
        this._pendingQuestions$.next(null);
        this._apiSuggestions$.next([]);
        this._pendingAttachments$.next([]);
        this._feedbackByMessageId.clear();
        this._feedback$.next(new Map(this._feedbackByMessageId));

        // Switch to the target conversation IMMEDIATELY so the open feels
        // instant: drop the current thread and show the clicked conversation's
        // title optimistically (from the cached list). The skeleton — driven by
        // historyLoading — covers the message fetch until it lands.
        this._messages$.next([]);
        // Clear the id up front so a failed load doesn't leave us pointed at the
        // PREVIOUS conversation (a follow-up send would otherwise append there).
        // Success sets it to the loaded conversation's id below.
        this.conversationId = null;
        const known = [...this._activeConversations$.value, ...this._archivedConversations$.value]
            .find((c) => c.id === conversationId);
        this._activeTitle$.next(known?.title ?? null);

        this._activeConversationScope = scope ?? null;
        // History is the only async source of Activity tasks; re-settled in both
        // handlers below.
        this._sandboxTasksLoaded$.next(false);
        this._service.loadConversation(conversationId, scope).subscribe({
            next: (res) => {
                this.conversationId = res.conversation.id;
                this._activeTitle$.next(res.conversation.title ?? null);
                const raw = res.messages ?? [];
                // Option B: CMA long-deliverable card rows (tagged sandboxRunId)
                // are lifted into the Activity task list, NOT the chat transcript.
                const deliverableRows = raw.filter((m) => !!m.sandboxRunId);
                this._messages$.next(
                    raw.filter((m) => !m.sandboxRunId).map((m) => this._mapHistoryMessage(m)),
                );
                this._rehydrateSandboxTasksFromHistory(deliverableRows);
                // A still-streaming stub means the turn is live — reattach instead of rendering a dead row.
                const liveStub = raw.find((m) => !m.sandboxRunId && m.streamState === 'streaming');
                if (liveStub) {
                    // Prefer the stashed live view: it carries the in-memory thoughts
                    // the history payload won't have until the turn completes.
                    const stashed = this._liveTurnCache.get(res.conversation.id);
                    if (stashed?.some((m) => m.messageId === liveStub.messageId && m.streamState === 'streaming')) {
                        this._messages$.next(stashed);
                    }
                    this._liveTurnCache.delete(res.conversation.id);
                    this._streamingAssistantMessageId = liveStub.messageId;
                    this._isSending$.next(true);
                    this._ensureChannelOpen(res.conversation.id, 45000);
                    // A re-joined turn has already missed its started event and may have
                    // missed its completed one: give it the same safety nets as a fresh turn.
                    this._lastStreamEventAt = Date.now();
                    this._armStreamWatchdog();
                    this._startStallPoll();
                } else {
                    this._liveTurnCache.delete(res.conversation.id);
                    this._isSending$.next(false);
                }
                // Fallback clear: a reload means whatever turn the notice was tracking is no longer this view's concern.
                this._turnInFlightNotice$.next(false);
                this._historyLoading$.next(false);
            },
            error: () => {
                this._historyLoading$.next(false);
                this._messages$.next([
                    ...this._messages$.value,
                    {
                        role: 'assistant',
                        content: 'Sorry — I couldn\'t open that conversation. It may have been archived or removed.',
                        timestamp: new Date(),
                        streamState: 'failed',
                    },
                ]);
                // Nothing more is coming for the task list on this load.
                this._sandboxTasksLoaded$.next(true);
            },
        });
    }

    /**
     * Map one persisted history row (contract shape) into the in-memory
     * `GableChatMessage` the drawer renders. Historical assistant rows are
     * always terminal, so `streamState` defaults to 'complete'. Thought
     * trails are reconstructed collapsed (the labor-illusion animation only
     * applies to live turns); metadata arrays default to empty.
     */
    private _mapHistoryMessage(m: GableConversationMessage): GableChatMessage {
        const timestamp = m.timestamp ? new Date(m.timestamp) : new Date();
        if (m.role === 'user') {
            return { role: 'user', content: m.content ?? '', timestamp, messageId: m.messageId, sequence: m.sequence };
        }
        const thoughts: GableThought[] = (m.thoughts ?? []).map((t, i) => ({
            id: t.id ?? `history-${m.messageId}-${i}`,
            label: t.label,
            ok: t.ok ?? true,
            kind: t.kind,
        }));
        // An empty non-streaming assistant row is a dead turn however it is labeled — never render it as nothing.
        const deadEmpty = m.streamState !== 'streaming' && (m.content ?? '').trim() === ''
            && (m.toolCards ?? []).length === 0;
        return {
            role: 'assistant',
            content: deadEmpty ? GABLE_TURN_FAILED_COPY : (m.content ?? ''),
            timestamp,
            messageId: m.messageId,
            sequence: m.sequence,
            streamState: deadEmpty ? 'failed' : (m.streamState ?? 'complete'),
            citations: m.citations ?? [],
            toolCards: m.toolCards ?? [],
            thoughts,
            thoughtsCollapsed: thoughts.length > 0,
            interactionId: m.interactionId ?? undefined,
            ...(m.durationMs ? { streamStartedAt: timestamp.getTime() - m.durationMs, streamEndedAt: timestamp.getTime() } : {}),
        };
    }

    /**
     * Rename a conversation and patch it into whichever list it lives in. The
     * active-title header updates too when the renamed convo is the one open.
     * Returns a cold observable (state patched via `tap`) so the CALLER owns
     * the single subscription — avoids a double HTTP fire and surfaces
     * success/error via the Growler at the call site.
     */
    renameConversation(conversationId: number, title: string): Observable<unknown> {
        return this._service.renameConversation(conversationId, title, this._scopeForConversation(conversationId)).pipe(
            tap((res: GableConversationMutationResponse) => {
                const next = res.conversation.title ?? null;
                this._patchConversationInLists(conversationId, (c) => ({ ...c, title: next }));
                if (this.conversationId === conversationId) {
                    this._activeTitle$.next(next);
                }
            }),
        );
    }

    /** Archive a conversation — drop it from active, add it to archived. */
    archiveConversation(conversationId: number): Observable<unknown> {
        return this._service.archiveConversation(conversationId, this._scopeForConversation(conversationId)).pipe(
            tap(() => {
                const moved = this._activeConversations$.value.find((c) => c.id === conversationId);
                this._activeConversations$.next(
                    this._activeConversations$.value.filter((c) => c.id !== conversationId),
                );
                if (moved) {
                    this._archivedConversations$.next([
                        { ...moved, status: 'archived' },
                        ...this._archivedConversations$.value.filter((c) => c.id !== conversationId),
                    ]);
                }
                // Archiving the open conversation resets the drawer to a fresh chat.
                if (this.conversationId === conversationId) {
                    this.startNewConversation();
                }
            }),
        );
    }

    /** Unarchive a conversation — drop it from archived, add it back to active. */
    unarchiveConversation(conversationId: number): Observable<unknown> {
        return this._service.unarchiveConversation(conversationId, this._scopeForConversation(conversationId)).pipe(
            tap(() => {
                const moved = this._archivedConversations$.value.find((c) => c.id === conversationId);
                this._archivedConversations$.next(
                    this._archivedConversations$.value.filter((c) => c.id !== conversationId),
                );
                if (moved) {
                    this._activeConversations$.next([
                        { ...moved, status: 'active' },
                        ...this._activeConversations$.value.filter((c) => c.id !== conversationId),
                    ]);
                }
            }),
        );
    }

    /**
     * The tenant a per-conversation write has to be addressed to. The lists are
     * fetched with `allScopes`, so a row on screen in portfolio scope may live
     * in an org (and vice versa) — sending its rename/archive to the drawer's
     * current scope 404s server-side, which surfaced as a bare "unexpected
     * error" toast. Falls back to the open conversation's scope for the header
     * actions, and finally to `undefined` = "whatever scope the drawer is in".
     */
    private _scopeForConversation(conversationId: number): GableConversationScope | undefined {
        const known = [...this._activeConversations$.value, ...this._archivedConversations$.value]
            .find((c) => c.id === conversationId);
        if (known?.scope) {
            return known.scope;
        }

        return this.conversationId === conversationId
            ? this._activeConversationScope ?? undefined
            : undefined;
    }

    /** Apply a transform to a conversation wherever it lives (active or archived). */
    private _patchConversationInLists(
        conversationId: number,
        transform: (c: GableConversationSummary) => GableConversationSummary,
    ): void {
        this._activeConversations$.next(
            this._activeConversations$.value.map((c) => (c.id === conversationId ? transform(c) : c)),
        );
        this._archivedConversations$.next(
            this._archivedConversations$.value.map((c) => (c.id === conversationId ? transform(c) : c)),
        );
    }

    get messages(): GableChatMessage[] {
        return this._messages$.value;
    }

    get isSending(): boolean {
        return this._isSending$.value;
    }

    get pendingQuestions(): GablePendingQuestions | null {
        return this._pendingQuestions$.value;
    }

    /** Load pending proposals from the server (idempotent; non-fatal on error). */
    loadPendingProposals(): void {
        // Skip when Gable isn't enabled for the current org (SD-7937) or the user
        // isn't in the review audience (SD-8321) — the channel subscribe below
        // would 403 /broadcasting/auth for a non-super user (see _inReviewAudience).
        // PMC scope bypasses the JWT-org flag (core checks the PMC's flags, SD-9074 wave 2).
        if (!this._historyFlagOk || !this._inReviewAudience || this._refreshHalted) {
            // Settled, not pending: this user's queue is never going to load, and
            // a consumer waiting on the flag would wait forever.
            this._pendingProposalsLoaded$.next(true);
            return;
        }
        // Ensure the realtime channel is live for this org (idempotent) so a
        // background suggestion staged elsewhere refreshes the queue + badge
        // without a reopen (SD-8104).
        this._subscribeToSuggestionsChannel();
        const issuedForScope = this._currentScopeStamp();
        this._service.listPendingProposals().subscribe({
            next: (res) => {
                if (this._isStaleGableResponse(issuedForScope)) {
                    return;
                }
                this._pendingProposals$.next(res.proposals ?? []);
                this._pendingProposalsLoaded$.next(true);
            },
            error: (err) => {
                if (this._isStaleGableResponse(issuedForScope)) {
                    return;
                }
                this._haltRefreshIfUnauthorized(err);
                this._pendingProposals$.next([]);
                this._pendingProposalsLoaded$.next(true);
            },
        });
    }

    /**
     * Subscribe to the org's background-suggestions channel (SD-8104) so a
     * suggestion staged out-of-band refreshes the review queue + badge live.
     * Idempotent: re-pointing to a new org leaves the previous channel first.
     * The event is a thin signal — we refetch the normalized pending list
     * rather than trusting a payload shape, so new suggestion sources need no
     * client change. Channel name is lowercase-with-hyphens (Pusher-safe).
     */
    private _subscribeToSuggestionsChannel(): void {
        const orgId = this._orgContext.currentOrgId;
        if (!orgId) {
            return;
        }
        const channel = `gable-suggestions-${orgId}`;
        if (this._suggestionsChannel === channel) {
            return;
        }
        this._unsubscribeFromSuggestionsChannel();
        this._suggestionsChannel = channel;
        // Bound once and listened to twice rather than chained: Echo's `listen()` happens to return
        // the channel, but relying on that makes the second binding silently depend on the first
        // one's return value.
        const suggestions = this._echo.private(channel);

        suggestions.listen('.suggestion.staged', () => {
            this._suggestionsChanged$.next({ kind: 'staged', formSubmissionIds: [] });
            this.loadPendingProposals();
        });

        // SD-9015: the mirror image — staff handled the request themselves, so suggestions for it
        // were retired server-side. Refetching through the same path means every existing consumer
        // of `pendingProposals$` (the drawer queue, the Ask badge, the Requests detail AI card)
        // drops the stale card with no change of its own; without this they keep showing an action
        // that no longer exists until the next reopen.
        suggestions.listen('.suggestion.superseded', (payload: { formSubmissionIds?: number[] }) => {
            this._suggestionsChanged$.next({
                kind: 'superseded',
                formSubmissionIds: payload?.formSubmissionIds ?? [],
            });
            this.loadPendingProposals();
        });

        // Gable actually EXECUTED something. Unlike staged/superseded, the
        // record itself changed — the reply is posted, the status is set — so a page showing
        // that request is now displaying stale data next to a card that says "done".
        // `entityId` is the FormSubmission the action was about; absent when the action has no
        // single subject, in which case listeners should refresh their list rather than a row.
        suggestions.listen('.action.committed', (payload: { entityId?: number; actionType?: string; permissionModules?: string[] }) => {
            const modules = Array.isArray(payload?.permissionModules) ? payload.permissionModules : [];
            this._suggestionsChanged$.next({
                kind: 'committed',
                // Requests surfaces read this; it is only a FormSubmission id when the write was a requests one.
                formSubmissionIds: payload?.entityId && (modules.length === 0 || modules.includes('forms')) ? [payload.entityId] : [],
                actionType: payload?.actionType,
                permissionModules: modules,
                entityId: payload?.entityId ?? null,
            });
            this.loadPendingProposals();
            if (payload?.actionType === 'manage_gable_chat') {
                this.refreshConversations(true);
            }
        });
    }

    private _unsubscribeFromSuggestionsChannel(): void {
        if (this._suggestionsChannel) {
            this._echo.leave(this._suggestionsChannel);
            this._suggestionsChannel = null;
        }
    }

    /**
     * True when a refresh response belongs to a superseded org context — the user
     * switched orgs while the request was in flight (SD-7898). Mirrors the
     * stale-context guard in `refresh-token.interceptor` (SD-7885/SD-7148): a late
     * prior-org response — success OR 401 — must be ignored so it neither overwrites
     * the new org's freshly-loaded list nor re-arms the 401 halt latch against the
     * now-current context (which would silently disable refresh until the next switch).
     */
    private _isStaleGableResponse(issuedForScope: string): boolean {
        return issuedForScope !== this._currentScopeStamp();
    }

    /** Org id + Gable scope key, so an org switch OR a scope crossing discards late responses (SD-9208). */
    private _currentScopeStamp(): string {
        return `${this._orgContext.currentOrgId}|${this._scope.scopeKey}`;
    }

    /**
     * Latch the stop-on-401 guard when a refresh request comes back Unauthorized
     * (SD-7898). A 401 here means the request carried a token the server has
     * blacklisted (typically mid org-switch); continuing to re-fire it just logs
     * more `token_blacklisted` audit rows. The latch clears on the next
     * org-context change (see `_resetForOrgContextChange`).
     */
    private _haltRefreshIfUnauthorized(error: unknown): void {
        if (error instanceof HttpErrorResponse && error.status === 401) {
            this._refreshHalted = true;
        }
    }

    /**
     * Hard-reset the drawer to a clean slate for a new org context, then re-hydrate
     * under the new token (SD-7898). Fired by OrgContextService when the viewed org
     * changes (switch / demo enter-exit). Stops the stale-token request storm and
     * prevents the previous org's conversations/proposals from lingering in the new
     * org's drawer.
     */
    private _resetForOrgContextChange(): void {
        // Tears down the active stream and clears messages, pending questions, the
        // active title and the conversation id.
        this.startNewConversation();

        // Clear what startNewConversation() does not: the previous org's proposals,
        // its recent/archived lists, and any active history search term.
        this._pendingProposals$.next([]);
        // The new org's queue is not in yet — `loadPendingProposals()` below
        // re-settles it.
        this._pendingProposalsLoaded$.next(false);
        this._activeConversations$.next([]);
        this._archivedConversations$.next([]);
        this._conversationSearch = '';
        this._archivedSearch = '';
        this._archivedSort = 'recent';
        this._archivedScopeFilter = null;
        this._archivedScopes$.next([]);
        this._archivedPage = 1;
        this._hasMoreArchived$.next(false);
        // Without this, an org switch landing mid-fetch latches the panel on a
        // permanent "Loading…" row that no user action can clear.
        this._archivedLoading$.next(false);

        // The 401 latch belonged to the OLD context — release it so the new org loads.
        this._refreshHalted = false;

        // Tell the host to drop the archived panel's client-side filter/sort/term,
        // now that the rows they applied to are gone.
        this._archivedReset$.next();

        // Re-hydrate for the new org (mirrors the host's ngOnInit bootstrap). Runs
        // under the new token since OrgContextService emits after store().
        this.loadPendingProposals();
        this.refreshConversations();
    }

    // -------------------------------------------------------------------------
    // Send (streaming, with blocking fallback)
    // -------------------------------------------------------------------------

    sendMessage(text: string): void {
        const typed = (text ?? '').trim();
        const count = this._pendingAttachments$.value.length;
        const trimmed = typed || (count ? (count === 1 ? 'I attached a file.' : `I attached ${count} files.`) : '');
        if (!trimmed) {
            return;
        }
        if (this._isSending$.value) {
            this._turnInFlightNotice$.next(true);
            return;
        }
        // Mid-question-card free-text discards the open batch.
        if (this._pendingQuestions$.value !== null) {
            this._pendingQuestions$.next(null);
        }
        const pending = this._pendingAttachments$.value;
        const attachments = pending.length ? pending : undefined;
        this._pendingAttachments$.next([]);
        const attachmentRefs = attachments?.map((a) => (a.documentId ? { documentId: a.documentId } : { uploadedFileId: a.uploadedFileId }));
        // A fresh send (as opposed to a busy auto-resend) always starts its own retry ladder.
        this._clearBusyRetry();
        this._busyRetryAttempt = 0;
        this._attemptSend(trimmed, attachmentRefs, attachments, this._randomTurnId(), this.conversationId);
    }

    /**
     * One POST attempt of a turn — the original send, or a `gable_busy` auto-resend of the
     * SAME `clientTurnId` (SD-10526 section 3). Pushes the optimistic user bubble, then
     * either begins streaming or handles the structured error shapes core can return.
     * `conversationId` is threaded explicitly (rather than read off `this.conversationId`)
     * because a busy retry must target the conversation core named in the 503 body — using
     * the field this service had BEFORE the first attempt would fork a new conversation on
     * every retry of a brand-new chat.
     */
    private _attemptSend(
        text: string,
        attachmentRefs: GableAttachmentRef[] | undefined,
        attachments: GableChatAttachment[] | undefined,
        clientTurnId: string,
        conversationId: number | null,
    ): void {
        this._messages$.next([
            ...this._messages$.value,
            { role: 'user', content: text, timestamp: new Date(), attachments },
        ]);
        this._draftMessage$.next('');
        this._pendingAttachments$.next([]);
        this._isSending$.next(true);
        this._turnInFlightNotice$.next(false);
        this._teardownStream();

        // A null conversationId means this send forks a brand-new conversation,
        // which the server will title post-completion. Flag it so the completed
        // handler holds the channel open for the (slow) `ai.conversation.titled`
        // broadcast. Set AFTER _teardownStream() since that resets the flag.
        this._awaitingFirstTitle = conversationId === null;

        this._service.chatStream(text, conversationId, attachmentRefs, this._activeConversationScope ?? undefined, clientTurnId).subscribe({
            next: (handshake) => {
                // Still a 202 — but core never forwarded the turn (box down, SD-10526). Real
                // conversation/rows, no stream ever started: end it now, do not subscribe.
                if (handshake.streamState === 'failed') {
                    this._busyRetryAttempt = 0;
                    this.conversationId = handshake.conversationId;
                    this._isSending$.next(false);
                    this._pushFailedMessage(handshake.message || GABLE_TURN_FAILED_COPY);
                    return;
                }
                this._busyRetryAttempt = 0;
                const isNew = this.conversationId === null;
                this.conversationId = handshake.conversationId;
                // A brand-new conversation goes into the sidebar NOW — waiting for the
                // titled broadcast made it invisible until the turn ended (or forever, on a failed turn).
                if (isNew && !this._activeConversations$.value.some((c) => c.id === handshake.conversationId)) {
                    this._activeConversations$.next([
                        {
                            id: handshake.conversationId,
                            // Fall back to the question itself so a row is never nameless;
                            // the generated title replaces it when it lands.
                            title: handshake.title ?? text.slice(0, 100),
                            status: 'active',
                            lastMessageAt: new Date().toISOString(),
                            messageCount: 1,
                        },
                        ...this._activeConversations$.value,
                    ]);
                }
                if (handshake.title && this._activeTitle$.value === null) {
                    this._activeTitle$.next(handshake.title);
                }
                this._beginStreamingTurn(handshake.conversationId, handshake.assistantMessageId);
            },
            // A 422 ends immediately — re-posting the same message would just 422 again.
            error: (err: unknown) => {
                if (err instanceof HttpErrorResponse && err.status === 422) {
                    this._isSending$.next(false);
                    this._pushFailedMessage(this._firstValidationMessage(err));
                    return;
                }
                // Frozen contract: core returns 409 turn_in_flight when the box already has a
                // turn running for this conversation. Calm inline notice, not the red error state —
                // the running turn is still alive server-side.
                if (err instanceof HttpErrorResponse && err.status === 409 && err.error?.error === 'turn_in_flight') {
                    this._isSending$.next(false);
                    this._turnInFlightNotice$.next(true);
                    // The optimistic bubble already posted for this send — pop it and hand the text back to the composer.
                    this._popOptimisticBubble(text, attachments);
                    // _teardownStream() above already closed the channel — reopen it so the still-running turn's events keep flowing.
                    if (this.conversationId !== null) {
                        this._ensureChannelOpen(this.conversationId, 45000);
                    }
                    return;
                }
                // 403 = the gate said no (PMC lane: pmc-dashboard + ai-gable flags on the PMC
                // itself) — retrying can't help, so say what's wrong instead of "couldn't reach".
                if (err instanceof HttpErrorResponse && err.status === 403) {
                    this._isSending$.next(false);
                    this._pushFailedMessage(this._scope.isPmcScoped
                        ? 'Gable isn\'t enabled for your management company\'s portfolio view yet. You can open one of your communities and ask from there.'
                        : 'Gable isn\'t enabled for you in this organization.');
                    return;
                }
                // Org/day spend cap (SD-10526) — not retryable, unlike gable_busy.
                if (err instanceof HttpErrorResponse && err.status === 429 && err.error?.error === 'gable_daily_limit') {
                    this._isSending$.next(false);
                    this._popOptimisticBubble(text, attachments);
                    this._pushFailedMessage(
                        typeof err.error?.message === 'string' ? err.error.message : GABLE_DAILY_LIMIT_COPY,
                    );
                    return;
                }
                // Frozen contract (SD-10526 section 3): core waits + retries once against the box,
                // then gives up and returns 503 gable_busy with the box's retryAfterSeconds AND the
                // conversationId the (possibly brand-new) conversation resolved to — the retry must
                // target THAT conversation, not fork another one. Calm inline notice + auto-resend
                // of this SAME clientTurnId, capped at 3 attempts total.
                if (err instanceof HttpErrorResponse && err.status === 503 && err.error?.error === 'gable_busy') {
                    this._isSending$.next(false);
                    this._popOptimisticBubble(text, attachments);
                    this._busyRetryAttempt += 1;
                    if (this._busyRetryAttempt >= GABLE_BUSY_MAX_ATTEMPTS) {
                        this._busyRetryAttempt = 0;
                        this._pushFailedMessage(GABLE_BUSY_FINAL_COPY);
                        return;
                    }
                    const retryAfterSeconds = err.error?.retryAfterSeconds;
                    const seconds = typeof retryAfterSeconds === 'number' && retryAfterSeconds > 0
                        ? retryAfterSeconds
                        : GABLE_BUSY_DEFAULT_RETRY_SECONDS;
                    const busyConversationId = typeof err.error?.conversationId === 'number' ? err.error.conversationId : conversationId;
                    this._armBusyRetry(seconds, () => this._attemptSend(text, attachmentRefs, attachments, clientTurnId, busyConversationId));
                    return;
                }
                // A structured, synchronous failure core returned on this same request (not one of
                // the recoverable shapes above) — the turn never started server-side, so end it now.
                // POST /chat is gone from core; there is no blocking fallback to try instead, and
                // waiting on a broadcast makes no sense for a turn that was never forwarded.
                this._isSending$.next(false);
                this._busyRetryAttempt = 0;
                this._pushFailedMessage(
                    err instanceof HttpErrorResponse && typeof err.error?.message === 'string'
                        ? err.error.message
                        : GABLE_TURN_FAILED_COPY,
                );
            },
        });
    }

    /** Pops the optimistic user bubble this attempt posted and hands the text/attachments back to the composer. */
    private _popOptimisticBubble(text: string, attachments: GableChatAttachment[] | undefined): void {
        const current = this._messages$.value;
        const lastMessage = current[current.length - 1];
        if (lastMessage?.role === 'user' && lastMessage.content === text) {
            this._messages$.next(current.slice(0, -1));
        }
        this._draftMessage$.next(text);
        if (attachments) {
            this._pendingAttachments$.next(attachments);
        }
    }

    /** Arms the countdown + auto-resend timer for a `gable_busy` retry; `onFire` performs the resend. */
    private _armBusyRetry(seconds: number, onFire: () => void): void {
        this._clearBusyRetry();
        let remaining = Math.round(seconds);
        this._busySecondsRemaining$.next(remaining);
        this._busyCountdownInterval = setInterval(() => {
            remaining -= 1;
            this._busySecondsRemaining$.next(Math.max(remaining, 0));
        }, 1000);
        this._busyRetryTimeout = setTimeout(() => {
            this._clearBusyRetry();
            onFire();
        }, seconds * 1000);
    }

    private _clearBusyRetry(): void {
        if (this._busyRetryTimeout !== null) {
            clearTimeout(this._busyRetryTimeout);
            this._busyRetryTimeout = null;
        }
        if (this._busyCountdownInterval !== null) {
            clearInterval(this._busyCountdownInterval);
            this._busyCountdownInterval = null;
        }
        this._busySecondsRemaining$.next(null);
    }

    private _stashLiveTurn(): void {
        if (this.conversationId !== null
            && this._messages$.value.some((m) => m.streamState === 'streaming')) {
            this._liveTurnCache.set(this.conversationId, this._messages$.value);
        }
    }

    private _pushFailedMessage(content: string): void {
        this._messages$.next([
            ...this._messages$.value,
            { role: 'assistant', content, timestamp: new Date(), streamState: 'failed' },
        ]);
    }

    /** First Laravel validation message off a 422, else the top-level message. */
    private _firstValidationMessage(err: HttpErrorResponse): string {
        const errors = err.error?.errors as Record<string, string[]> | undefined;
        const firstKey = errors ? Object.keys(errors)[0] : undefined;
        const firstMsg = firstKey ? errors?.[firstKey]?.[0] : undefined;
        return firstMsg ?? err.error?.message ?? 'Sorry — that message couldn\'t be sent. Please try again.';
    }

    private _beginStreamingTurn(conversationId: number, assistantMessageId: number): void {
        this._streamingAssistantMessageId = assistantMessageId;
        const existing = this._messages$.value.find(
            (m) => m.role === 'assistant' && m.messageId === assistantMessageId,
        );
        if (!existing) {
            this._messages$.next([
                ...this._messages$.value,
                {
                    role: 'assistant',
                    content: '',
                    timestamp: new Date(),
                    messageId: assistantMessageId,
                    streamState: 'streaming',
                    streamStartedAt: Date.now(),
                },
            ]);
        }

        const handle = this._streamService.open(conversationId);
        this._streamHandle = handle;
        this._streamSub = handle.events$.subscribe({
            next: (event) => this._handleStreamEvent(event),
        });
        // Seeded at open so a brand-new stream never reads as silent.
        this._lastStreamEventAt = Date.now();
        // Armed here, not on `ai.stream.started`: a turn that delivers NO events
        // at all is exactly the case the safety net has to cover, and arming on
        // the first event means the net depends on the channel that just failed.
        this._armStreamWatchdog();
        this._startStallPoll();
        // A broadcast published before this client's Echo subscription actually took hold
        // (SD-10526) would otherwise sit unnoticed until the next poll tick or the 180s
        // watchdog — ask core once, right now, whether the turn already has a terminal
        // streamState.
        this._checkServerForFinishedTurn();
    }

    private _startStallPoll(): void {
        this._stopStallPoll();
        this._stallPoll = setInterval(() => {
            if (this._isSending$.value && this._streamHasGoneSilent()) {
                this._checkServerForFinishedTurn();
            }
        }, STALL_POLL_MS);
    }

    private _stopStallPoll(): void {
        if (this._stallPoll !== null) {
            clearInterval(this._stallPoll);
            this._stallPoll = null;
        }
    }

    /**
     * The saved message is the source of truth; the stream is a preview. If core says
     * the turn finished, finalize now and pull the transcript rather than wait for a
     * completed event that may never come.
     */
    private _checkServerForFinishedTurn(): void {
        const conversationId = this.conversationId;
        const messageId = this._streamingAssistantMessageId;
        if (conversationId === null || messageId === null || this._stallCheckInFlight) {
            return;
        }
        this._stallCheckInFlight = true;
        this._service.fetchToolCards(conversationId, messageId).subscribe({
            next: (saved) => {
                this._stallCheckInFlight = false;
                if (saved.streamState === 'complete' || saved.streamState === 'failed' || saved.streamState === 'cancelled') {
                    this._forceFinalizeStuckStream();
                }
            },
            error: () => {
                this._stallCheckInFlight = false;
            },
        });
    }

    /**
     * User-driven stop: fired by the composer's interrupt button while a
     * turn is streaming. Posts a cancel signal scoped to the in-flight
     * assistant message id (the orchestrator's worker polls a cache flag
     * keyed on that id and short-circuits its Prism loop), then tears
     * down the local stream subscription so the UI freezes immediately
     * — the user shouldn't have to wait for the server to settle to
     * regain the composer. Whatever text streamed in so far stays in
     * the bubble; the stream-state flips to 'complete' so footer
     * affordances (thumbs, follow-ups) render.
     */
    cancelStream(): void {
        const msgId = this._streamingAssistantMessageId;
        if (msgId === null) {
            // Nothing to cancel (race with stream completion / failed
            // before we tracked an id). Still clear sending state so the
            // composer unfreezes.
            this._isSending$.next(false);
            return;
        }
        this._service.cancelStream(msgId).subscribe({
            error: () => {
                // Best-effort. The local teardown below still happens.
            },
        });
        this._messages$.next(
            this._messages$.value.map((m) =>
                m.role === 'assistant' && m.messageId === msgId
                    ? { ...m, streamState: 'complete' as const, streamEndedAt: Date.now() }
                    : m,
            ),
        );
        this._isSending$.next(false);
        this._teardownStream();
        this._streamingAssistantMessageId = null;
    }

    private _handleStreamEvent(event: AiStreamEvent): void {
        // Keepalives prove the box process lives, not that the turn progresses — they
        // must not feed the stall detectors or a hung run becomes an immortal zombie.
        // Once the box has said the turn moved to the background, though, a keepalive is
        // exactly the progress there is: one core tool call can run two minutes with no
        // other event, and finalizing that as "stalled" is the bug SD-9787 fixes. A run
        // whose process really dies stops sending keepalives, and the server-side
        // gable:fail-stale-turns sweep clears the row within five minutes regardless.
        const isKeepalive = event.type === 'ai.stream.summary-delta' && event.phaseToolCallId === 'gable-keepalive';
        if (!isKeepalive || this._longRunNotice$.value !== null) {
            this._lastStreamEventAt = Date.now();
            // Real events re-arm the watchdog: it measures SILENCE, not total duration.
            if (this._streamWatchdog !== null) {
                this._armStreamWatchdog();
            }
        }
        const messages = this._messages$.value;
        switch (event.type) {
            case 'ai.stream.started': {
                this._armStreamWatchdog();
                const startedAt = Date.parse(event.startedAt);
                const exists = messages.some(
                    (m) => m.role === 'assistant' && m.messageId === event.messageId,
                );
                if (!exists) {
                    this._messages$.next([
                        ...messages,
                        {
                            role: 'assistant',
                            content: '',
                            timestamp: new Date(),
                            messageId: event.messageId,
                            streamState: 'streaming',
                            thoughts: [],
                            thoughtsCollapsed: false,
                            streamStartedAt: Number.isFinite(startedAt) ? startedAt : Date.now(),
                        },
                    ]);
                } else {
                    this._messages$.next(messages.map((m) =>
                        m.role === 'assistant' && m.messageId === event.messageId
                            ? {
                                ...m,
                                thoughts: m.thoughts ?? [],
                                thoughtsCollapsed: m.thoughtsCollapsed ?? false,
                                streamStartedAt: m.streamStartedAt
                                    ?? (Number.isFinite(startedAt) ? startedAt : Date.now()),
                            }
                            : m,
                    ));
                }
                break;
            }
            case 'ai.stream.text-delta': {
                this._messages$.next(messages.map((m) => {
                    if (m.role === 'assistant' && m.messageId === event.messageId) {
                        return { ...m, content: m.content + event.delta };
                    }
                    return m;
                }));
                break;
            }
            case 'ai.stream.narration': {
                const text = event.text ?? '';
                this._messages$.next(messages.map((m) => {
                    if (m.role !== 'assistant' || m.messageId !== event.messageId || text.trim() === '') {
                        return m;
                    }
                    const at = m.content.lastIndexOf(text);
                    const content = at < 0 ? m.content : (m.content.slice(0, at) + m.content.slice(at + text.length)).trimEnd();
                    const thought: GableThought = { id: `narration-${(m.thoughts ?? []).length}`, label: text.trim(), ok: true, kind: 'narration' };
                    return { ...m, content, thoughts: [...(m.thoughts ?? []), thought] };
                }));
                break;
            }
            case 'ai.stream.tool-call': {
                // event.messageId here is the TOOL row's id, not the
                // assistant bubble's. Attach the thought to the
                // currently-streaming assistant message instead.
                const streamingId = this._streamingAssistantMessageId;
                this._messages$.next(messages.map((m) => {
                    if (m.role === 'assistant' && streamingId !== null && m.messageId === streamingId) {
                        const next = [...(m.thoughts ?? []), { id: event.toolCallId, label: event.label }];
                        return { ...m, thoughts: next };
                    }
                    return m;
                }));
                break;
            }
            case 'ai.stream.tool-result': {
                const streamingId = this._streamingAssistantMessageId;
                this._messages$.next(messages.map((m) => {
                    if (m.role === 'assistant' && streamingId !== null && m.messageId === streamingId && m.thoughts) {
                        const next = m.thoughts.map((t) =>
                            t.id === event.toolCallId ? { ...t, ok: event.ok } : t,
                        );
                        return { ...m, thoughts: next };
                    }
                    return m;
                }));
                break;
            }
            case 'ai.stream.thinking-delta': {
                // Backend no longer broadcasts raw thinking (it's leaky).
                // Keep this case as a no-op so any in-flight stream from an
                // older server build doesn't render raw CoT in the UI.
                break;
            }
            case 'ai.stream.summary-delta': {
                const streamingId = this._streamingAssistantMessageId;
                const targetId = event.messageId ?? streamingId;
                this._messages$.next(messages.map((m) => {
                    if (m.role !== 'assistant' || targetId === null || m.messageId !== targetId) {
                        return m;
                    }
                    const existing = m.thoughts ?? [];
                    const summaryEntry = {
                        id: 'summary:' + (event.phaseToolCallId ?? 'final:' + Date.now()),
                        label: event.summary,
                        kind: 'summary' as const,
                    };
                    // Keepalive events keep the stream warm but render nothing.
                    if (event.phaseToolCallId === 'gable-keepalive') {
                        return m;
                    }
                    if (event.phaseToolCallId === 'gable-long-run') {
                        this._longRunNotice$.next(event.summary);
                        return { ...m, longRunNotice: event.summary, thoughtsCollapsed: true };
                    }
                    // If the summary precedes a known tool call, splice it
                    // right before that tool row so visual order matches the
                    // "thought before action" flow. Otherwise append.
                    if (event.phaseToolCallId) {
                        const idx = existing.findIndex((t) => t.id === event.phaseToolCallId);
                        if (idx >= 0) {
                            const next = [
                                ...existing.slice(0, idx),
                                summaryEntry,
                                ...existing.slice(idx),
                            ];
                            return { ...m, thoughts: next };
                        }
                    }
                    return { ...m, thoughts: [...existing, summaryEntry] };
                }));
                break;
            }
            case 'ai.stream.completed': {
                this._messages$.next(messages.map((m) => {
                    if (m.role !== 'assistant') {
                        return m;
                    }
                    const isPrimary = m.messageId === event.messageId;
                    const isOrphanStream = !isPrimary && m.streamState === 'streaming';
                    if (!isPrimary && !isOrphanStream) {
                        return m;
                    }
                    const settledThoughts = (m.thoughts ?? []).map((t) =>
                        t.ok === undefined ? { ...t, ok: true } : t,
                    );
                    return {
                        ...m,
                        streamState: 'complete' as const,
                        toolsUsed: isPrimary ? (event.toolsUsed ?? []) : (m.toolsUsed ?? []),
                        streamEndedAt: Date.now(),
                        thoughts: settledThoughts,
                        // Dima: thoughts are visible WHILE streaming (the user
                        // watches the work), then COLLAPSE by default once the
                        // response is ready — expandable via the caret. Respect a
                        // user's explicit mid-stream collapse (don't re-open it).
                        thoughtsCollapsed: m.thoughtsCollapsed || settledThoughts.length > 0,
                        interactionId: isPrimary
                            ? (event.aiInteractionEventId ?? m.interactionId)
                            : m.interactionId,
                        toolCards: isPrimary && ((event.toolCards as GableToolCard[] | undefined)?.length ?? 0) > 0
                            ? (event.toolCards as GableToolCard[])
                            : m.toolCards,
                    };
                }));
                this._apiSuggestions$.next(event.followUpSuggestions ?? []);
                // The titled broadcast can fire before this client subscribed (title now
                // generates at turn start) — refetch so the sidebar never keeps "New chat".
                if (this._activeTitle$.value === null) {
                    this.refreshConversations();
                }

                // Pusher's ~10KB per-event limit silently drops big
                // toolCards payloads (a full P&L row tree can run 40–80KB).
                // When the model used a tool that should produce a card
                // but the broadcast came through with none, fall back to
                // the REST endpoint that reads the persisted card from
                // the assistant message's metadata.
                const broadcastCards = (event.toolCards ?? []) as GableToolCard[];
                const cardProducingTools = new Set<string>(CARD_PRODUCING_TOOLS);
                // propose_* covers the box's per-action tools (propose_send_violation_notice,
                // propose_approve_payable, …) — one tool per ActionDefinition since 2026-07-11.
                const usedCardTool = (event.toolsUsed ?? []).some(
                    (t) => cardProducingTools.has(t) || t.startsWith('propose_'),
                );
                if (this.conversationId !== null) {
                    this._reconcileMessageFromServer(
                        this.conversationId,
                        event.messageId,
                        broadcastCards.length === 0 && usedCardTool,
                    );
                }

                // Every text delta was missed (dropped/oversized events, socket blip):
                // pull the persisted answer instead of leaving a permanently empty bubble.
                const finalized = this._messages$.value.find(
                    (m) => m.role === 'assistant' && m.messageId === event.messageId,
                );
                if (
                    finalized !== undefined
                    && (finalized.content ?? '').trim() === ''
                    && (event.pendingQuestions ?? null) === null
                    && !usedCardTool
                    && this.conversationId !== null
                ) {
                    this._backfillFromServer(this.conversationId);
                }

                const incoming = (event.pendingProposals ?? []) as GablePendingProposal[];
                if (incoming.length > 0) {
                    const seen = new Set(this._pendingProposals$.value.map((p) => p.id));
                    const additions = incoming.filter((p) => !seen.has(p.id));
                    if (additions.length > 0) {
                        this._pendingProposals$.next([...this._pendingProposals$.value, ...additions]);
                    }
                }
                const completedQuestions = event.pendingQuestions ?? null;
                if (
                    completedQuestions !== null
                    && (this._pendingQuestions$.value === null
                        || this._pendingQuestions$.value.batchId !== completedQuestions.batchId)
                ) {
                    this._pendingQuestions$.next(completedQuestions);
                }
                this._disarmStreamWatchdog();
                this._stopStallPoll();
                this._isSending$.next(false);
                // The re-attached turn (if any) settled — clear the stale 409 notice.
                this._turnInFlightNotice$.next(false);
                // Confirmed here and nowhere else: this is the point the turn has
                // reached its terminal state in front of the customer. Core
                // otherwise never learns whether what it published landed.
                this._errorReporter.confirmTurnDelivered(this.conversationId, event.messageId);

                // SD-7240: a sandbox `build_document` finishes asynchronously
                // (minutes later) on this same channel. The backend broadcasts
                // a definitive `ai.sandbox.progress` ('queued') the moment one
                // is dispatched — synchronously, mid-turn, so it ALWAYS lands
                // before this completed event and the pending set below is
                // already populated. (No tool-heuristics: inferring "a sandbox
                // doc is coming" from toolsUsed/card absence false-positives on
                // a failed board_summary and would hold the channel open
                // forever.)
                if (this._pendingSandboxConversationIds.size > 0) {
                    // Keep the channel open for the async sandbox result; the
                    // follow-ups event will also land here in the meantime.
                    break;
                }

                // Delay teardown so the follow-ups event (broadcast by a
                // separate post-completion Haiku job, typically lands
                // within 2–3s) has a chance to arrive on the channel
                // before we leave it. Without the delay we'd close the
                // subscription mid-flight and the chips would silently
                // never show up. A brand-new conversation also gets a
                // title from a slower post-completion job (~20–30s), so
                // hold the channel open longer in that case so the
                // `ai.conversation.titled` broadcast can land live.
                this._scheduleDelayedTeardown(this._awaitingFirstTitle ? 45000 : 8000);
                break;
            }
            case 'ai.stream.follow-ups': {
                // Per-result chips arrive after `ai.stream.completed` (the
                // suggester runs as a separate Haiku job). Swap the empty
                // chip list the completed event seeded in for these
                // specific suggestions. Only honor for the current
                // conversation's most-recent assistant message — late
                // events from earlier turns get ignored.
                const streamingId = this._streamingAssistantMessageId;
                const lastAssistant = [...messages].reverse().find((m) => m.role === 'assistant');
                const targetId = lastAssistant?.messageId ?? null;
                if (event.messageId === targetId || event.messageId === streamingId) {
                    // Empty is meaningful too — it clears any stale chips
                    // rather than letting a prior turn's suggestions linger.
                    this._apiSuggestions$.next(event.suggestions ?? []);
                    // Gable-box turns: the interaction id arrives on THIS late
                    // event (the box's completed broadcast predates telemetry)
                    // — patch it in so the thumbs affordance renders.
                    if (event.aiInteractionEventId != null) {
                        this._messages$.next(messages.map((m) =>
                            m.messageId === event.messageId && m.interactionId == null
                                ? { ...m, interactionId: event.aiInteractionEventId ?? undefined }
                                : m,
                        ));
                    }
                }
                break;
            }
            case 'ai.conversation.titled': {
                // Post-completion title job finished (SD-6742). Update the
                // header live for the open conversation and refresh the recent
                // list so the freshly-named chat appears without a reopen.
                if (event.conversationId === this.conversationId) {
                    this._activeTitle$.next(event.title);
                }
                // Patch the row in place — refreshConversations() is gated for non-supers.
                this._activeConversations$.next(this._activeConversations$.value.map(
                    (c) => c.id === event.conversationId ? { ...c, title: event.title } : c,
                ));
                this.refreshConversations();
                // Got what we were holding the channel open for. If the turn is still
                // streaming (fast title job), leave the channel alone: the completed
                // handler owns the close. Tearing down here dropped every later event.
                if (this._awaitingFirstTitle) {
                    this._awaitingFirstTitle = false;
                    if (!this._isSending$.value) {
                        this._scheduleDelayedTeardown(2000);
                    }
                }
                break;
            }
            case 'ai.stream.pending-questions': {
                if (
                    this._pendingQuestions$.value === null
                    || this._pendingQuestions$.value.batchId !== event.batchId
                ) {
                    this._pendingQuestions$.next({
                        batchId: event.batchId,
                        leadIn: event.leadIn,
                        questions: event.questions,
                    });
                }
                this._messages$.next(messages.map((m) => {
                    if (m.role === 'assistant'
                        && m.messageId === event.messageId
                        && m.streamState === 'streaming') {
                        return { ...m, streamState: 'complete' as const };
                    }
                    return m;
                }));
                break;
            }
            case 'ai.stream.failed': {
                this._errorReporter.report(
                    'stream_failed',
                    event.error || 'The assistant stream failed without a reason.',
                    {
                        context: 'gable-drawer:ai.stream.failed',
                        conversationId: event.conversationId,
                        messageId: event.messageId,
                    },
                );
                const friendly = event.error
                    ? `Sorry — the assistant ran into a problem: ${event.error}`
                    : 'Sorry — the assistant ran into a problem and couldn\'t finish that response.';
                this._messages$.next(messages.map((m) => {
                    if (m.role !== 'assistant') {
                        return m;
                    }
                    const isPrimary = m.messageId === event.messageId;
                    const isOrphanStream = !isPrimary && m.streamState === 'streaming';
                    if (!isPrimary && !isOrphanStream) {
                        return m;
                    }
                    return {
                        ...m,
                        streamState: 'failed' as const,
                        content: m.content && m.content.length > 0 ? m.content : friendly,
                        streamEndedAt: Date.now(),
                    };
                }));
                this._disarmStreamWatchdog();
                this._stopStallPoll();
                this._isSending$.next(false);
                // Defer teardown if a sandbox deliverable is still pending for
                // this conversation — the turn failing doesn't cancel an
                // already-detached generation job, and its ready/failed event
                // still needs the open channel.
                if (this._pendingSandboxConversationIds.size === 0) {
                    this._teardownStream();
                }
                break;
            }
            case 'ai.stream.channel-error': {
                // Transport failure, not a turn failure — the box is unaffected. Keep
                // isSending true (the bubble keeps its working state) and lean on the
                // stall poll's fetchToolCards check for the saved streamState, bounded
                // by the stream watchdog. Force one check now rather than waiting out
                // the poll interval.
                this._errorReporter.report(
                    'channel_auth_failed',
                    'Realtime channel dropped mid-turn; polling core for the saved streamState.',
                    { context: 'gable-drawer:ai.stream.channel-error', conversationId: event.conversationId },
                );
                if (this._isSending$.value) {
                    this._connectionLostNotice$.next(true);
                    this._checkServerForFinishedTurn();
                }
                break;
            }
            case 'ai.sandbox.progress': {
                // First progress milestone for a run is our trigger to show
                // that run's pending "generating…" card (the tool-result
                // `status: "generating"` is not broadcast on its own event).
                // Subsequent milestones update the same card's label live.
                // Multiple runs may be pending at once — each gets its own row.
                const runKey = this._sandboxRunKey(event.conversationId, event.runId);
                this._trackPendingSandboxRun(event.conversationId, runKey);
                // Option B: surface as a first-class Activity task, NOT a chat card.
                this._upsertSandboxTask(runKey, event.conversationId, event.label);
                // Re-arm on each milestone — on expiry we mark "taking longer",
                // never fail (delivery is reliable, just minutes-long).
                this._armSandboxWatchdog(event.conversationId, runKey);
                break;
            }
            case 'ai.sandbox.ready': {
                // Replace THIS run's pending card (if any) with the real
                // deliverable card, rendered from the frozen `card` payload
                // exactly like the inline board_summary deliverable card.
                // Other runs' pending cards are left untouched.
                const runKey = this._sandboxRunKey(event.conversationId, event.runId);
                this._disarmSandboxWatchdog(runKey);
                this._resolveSandboxTask(runKey, event.conversationId, event.card);
                this._untrackPendingSandboxRun(event.conversationId, runKey);
                this._maybeTeardownAfterSandbox();
                break;
            }
            case 'ai.sandbox.failed': {
                const runKey = this._sandboxRunKey(event.conversationId, event.runId);
                this._disarmSandboxWatchdog(runKey);
                this._failSandboxTask(runKey, event.conversationId, event.message);
                this._untrackPendingSandboxRun(event.conversationId, runKey);
                this._maybeTeardownAfterSandbox();
                break;
            }
            case 'ai.stream.gap': {
                // A gap is the transport dropping events under us — the recurring
                // flake that is otherwise only visible when a customer complains.
                this._errorReporter.report('stream_gap', 'Stream events were lost in transport.', {
                    context: 'gable-drawer:ai.stream.gap',
                    conversationId: event.conversationId,
                    messageId: event.messageId,
                });
                // Lost events may include the completed one; ask core now instead of
                // waiting for silence. The saved text wins at completion either way.
                this._checkServerForFinishedTurn();
                break;
            }
        }
    }

    // -------------------------------------------------------------------------
    // Sandbox deliverable cards (SD-7240)
    // -------------------------------------------------------------------------

    // --- Activity-tab tasks (option B) — long deliverables live in Activity, ---
    // --- not as timing-out chat cards. ----------------------------------------

    private _upsertSandboxTask(runKey: string, conversationId: number, label: string): void {
        const tasks = this._sandboxTasks$.value;
        if (tasks.some((t) => t.runKey === runKey)) {
            this._sandboxTasks$.next(tasks.map((t) =>
                t.runKey === runKey ? { ...t, title: label || t.title, state: 'running' as const } : t,
            ));
            return;
        }
        this._sandboxTasks$.next([
            { runKey, conversationId, title: label || 'Preparing analysis…', state: 'running', startedAt: new Date() },
            ...tasks,
        ]);
    }

    private _resolveSandboxTask(runKey: string, conversationId: number, readyCard: GableToolCard['payload'] | GableToolCard): void {
        const card = this._sandboxCardPayload(readyCard);
        const tasks = this._sandboxTasks$.value;
        if (tasks.some((t) => t.runKey === runKey)) {
            this._sandboxTasks$.next(tasks.map((t) =>
                t.runKey === runKey ? { ...t, state: 'ready' as const, card, slow: false, title: this._cardTitle(card, t.title) } : t,
            ));
            return;
        }
        // Create-if-missing: a dropped `progress` event (drawer closed during
        // dispatch, channel churn) must NOT swallow the finished deliverable —
        // the ready event alone is enough to surface it in Activity.
        this._sandboxTasks$.next([
            { runKey, conversationId, title: this._cardTitle(card), state: 'ready', card, startedAt: new Date() },
            ...tasks,
        ]);
    }

    private _failSandboxTask(runKey: string, conversationId: number, error: string): void {
        const tasks = this._sandboxTasks$.value;
        if (tasks.some((t) => t.runKey === runKey)) {
            this._sandboxTasks$.next(tasks.map((t) =>
                t.runKey === runKey ? { ...t, state: 'failed' as const, error } : t,
            ));
            return;
        }
        this._sandboxTasks$.next([
            { runKey, conversationId, title: 'Analysis', state: 'failed', error, startedAt: new Date() },
            ...tasks,
        ]);
    }

    /** A document ready event carries the bare payload; a ledger one carries the `{ tool, payload }` envelope. */
    private _sandboxCardPayload(card: GableToolCard['payload'] | GableToolCard): GableToolCard['payload'] {
        if (card && typeof card === 'object' && 'tool' in card && 'payload' in card
            && SANDBOX_TASK_TOOLS.includes(String((card as GableToolCard).tool))) {
            return (card as GableToolCard).payload;
        }
        return card as GableToolCard['payload'];
    }

    /** Pull a display title off a task card payload (falls back gracefully). */
    private _cardTitle(card: GableToolCard['payload'], fallback = 'Analysis'): string {
        const source = isLedgerIntelligenceCardPayload(card) ? card.result : card;
        const title = source && typeof source === 'object' && 'title' in source
            ? String((source as { title?: unknown }).title ?? '')
            : '';
        return title || fallback;
    }

    /**
     * Option B reload path: CMA long-deliverable cards persist as chat messages
     * tagged with `sandboxRunId`. On conversation load we lift each into the
     * Activity task list (deduped by runKey against any live task) so the
     * finished document survives a reload — and keep it OUT of the chat
     * transcript (see loadConversation). running-state tasks are in-memory only;
     * a card row is always terminal, so these rehydrate straight to 'ready'.
     */
    private _rehydrateSandboxTasksFromHistory(rows: GableConversationMessage[]): void {
        this._sandboxTasksLoaded$.next(true);
        if (rows.length === 0) {
            return;
        }
        const existing = this._sandboxTasks$.value;
        const additions: SandboxTask[] = [];
        for (const m of rows) {
            const runKey = m.sandboxRunId ?? undefined;
            if (!runKey
                || existing.some((t) => t.runKey === runKey)
                || additions.some((t) => t.runKey === runKey)) {
                continue;
            }
            const card = (m.toolCards ?? []).find((c) => SANDBOX_TASK_TOOLS.includes(c.tool))?.payload;
            additions.push({
                runKey,
                conversationId: this.conversationId ?? 0,
                title: this._cardTitle(card),
                state: 'ready',
                card,
                startedAt: m.timestamp ? new Date(m.timestamp) : new Date(),
            });
        }
        if (additions.length > 0) {
            this._sandboxTasks$.next([...additions, ...existing]);
        }
    }

    private _markSandboxTaskSlow(runKey: string): void {
        this._sandboxTasks$.next(this._sandboxTasks$.value.map((t) =>
            t.runKey === runKey && t.state === 'running' ? { ...t, slow: true } : t,
        ));
    }

    /** Remove a ready/failed task from the Activity list (user dismiss). */
    dismissSandboxTask(runKey: string): void {
        this._sandboxTasks$.next(this._sandboxTasks$.value.filter((t) => t.runKey !== runKey));
        if (runKey.startsWith(WORKFLOW_RUN_TASK_PREFIX)) {
            this._unwatchWorkflowRun(Number(runKey.slice(WORKFLOW_RUN_TASK_PREFIX.length)));
        }
    }

    /** A workflow run started from the Workflows panel shows in Activity until the poll here sees it finish. */
    trackWorkflowRun(runId: number, title: string): void {
        this._upsertSandboxTask(`${WORKFLOW_RUN_TASK_PREFIX}${runId}`, this.conversationId ?? 0, `Running ${title}`);
    }

    finishWorkflowRun(runId: number): void {
        this.dismissSandboxTask(`${WORKFLOW_RUN_TASK_PREFIX}${runId}`);
    }

    /** Track the run in Activity and poll for it from here, so the panel being destroyed mid-run still ends in a toast. */
    watchWorkflowRun(runId: number, workflowId: number, title: string): void {
        this.trackWorkflowRun(runId, title);
        if (this._watchedWorkflowRuns.has(runId)) {
            return;
        }
        this._watchedWorkflowRuns.set(runId, { workflowId, title, watchedAt: Date.now() });
        this._publishRunningWorkflowRuns();
        this._clearWorkflowRunPollTimer();
        this._armWorkflowRunPoll();
    }

    /** Toast for a run that has finished: same copy whether the poll saw it end or run-now returned it already done. */
    announceWorkflowRun(run: GableWorkflowRun, title: string): void {
        if (run.status === 'failed') {
            this._growler.error('Run failed', (run.error ?? '').trim() || title);
            return;
        }
        this._growler.success(`${title} finished`, runFinishDetail(run));
    }

    isWorkflowRunWatched(runId: number): boolean {
        return this._watchedWorkflowRuns.has(runId);
    }

    private _unwatchWorkflowRun(runId: number): void {
        if (!this._watchedWorkflowRuns.delete(runId)) {
            return;
        }
        this._publishRunningWorkflowRuns();
        if (this._watchedWorkflowRuns.size === 0) {
            this._stopWorkflowRunPoll();
        }
    }

    private _publishRunningWorkflowRuns(): void {
        this._runningWorkflowRunIds$.next(new Set(this._watchedWorkflowRuns.keys()));
    }

    /** No timer while the tab is hidden; `_onVisibilityChange` resumes the poll when it comes back. */
    private _armWorkflowRunPoll(): void {
        if (this._workflowRunPoll !== null || this._watchedWorkflowRuns.size === 0 || this._tabHidden) {
            return;
        }
        this._workflowRunPoll = setTimeout(() => {
            this._workflowRunPoll = null;
            this._pollWorkflowRuns();
        }, this._workflowRunPollDelayMs());
    }

    private _workflowRunPollDelayMs(): number {
        const newestWatch = Math.max(...Array.from(this._watchedWorkflowRuns.values()).map((w) => w.watchedAt));
        const age = Date.now() - newestWatch;
        if (age < WORKFLOW_RUN_POLL_QUICK_WINDOW_MS) {
            return WORKFLOW_RUN_POLL_QUICK_MS;
        }
        return age < WORKFLOW_RUN_POLL_STEADY_WINDOW_MS ? WORKFLOW_RUN_POLL_STEADY_MS : WORKFLOW_RUN_POLL_SLOW_MS;
    }

    private get _tabHidden(): boolean {
        return typeof document !== 'undefined' && document.visibilityState === 'hidden';
    }

    private _resumeWorkflowRunPoll(): void {
        if (this._watchedWorkflowRuns.size === 0 || this._workflowRunPoll !== null || this._workflowRunPollInFlight) {
            return;
        }
        this._pollWorkflowRuns();
    }

    private _clearWorkflowRunPollTimer(): void {
        if (this._workflowRunPoll !== null) {
            clearTimeout(this._workflowRunPoll);
            this._workflowRunPoll = null;
        }
    }

    private _stopWorkflowRunPoll(): void {
        this._clearWorkflowRunPollTimer();
        this._workflowRunPollErrors = 0;
    }

    /** One `GET /workflows/{id}/runs?page=1` per watched workflow per tick, however many of its runs are watched. */
    private _pollWorkflowRuns(): void {
        const workflowIds = Array.from(new Set(Array.from(this._watchedWorkflowRuns.values()).map((w) => w.workflowId)));
        if (workflowIds.length === 0) {
            return;
        }
        this._workflowRunPollInFlight = true;
        forkJoin(workflowIds.map((workflowId) => this._workflows.runs(workflowId, 1).pipe(
            map((page) => ({ workflowId, runs: page.runs ?? [] })),
            catchError(() => of({ workflowId, runs: null })),
        ))).subscribe((pages) => {
            this._workflowRunPollInFlight = false;
            const failed = new Set(pages.filter((p) => p.runs === null).map((p) => p.workflowId));
            const runs = pages.flatMap((p) => p.runs ?? []);
            this._reconcileWorkflowRuns(runs, failed);
            if (failed.size === 0) {
                this._workflowRunPollErrors = 0;
            } else if (++this._workflowRunPollErrors >= WORKFLOW_RUN_POLL_MAX_ERRORS) {
                this._abandonWorkflowRuns(WORKFLOW_RUN_UNREACHABLE_COPY, failed);
            }
            this._armWorkflowRunPoll();
        });
    }

    private _reconcileWorkflowRuns(runs: GableWorkflowRun[], skipWorkflowIds: ReadonlySet<number>): void {
        const byId = new Map<number, GableWorkflowRun>(runs.map((run) => [run.id, run]));
        const now = Date.now();
        for (const [runId, watched] of Array.from(this._watchedWorkflowRuns.entries())) {
            if (skipWorkflowIds.has(watched.workflowId)) {
                continue;
            }
            const hit = byId.get(runId);
            if (hit && (hit.status === 'queued' || hit.status === 'running')) {
                if (now - watched.watchedAt >= WORKFLOW_RUN_WATCH_CAP_MS) {
                    this.finishWorkflowRun(runId);
                }
                continue;
            }
            this.finishWorkflowRun(runId);
            if (hit) {
                this.announceWorkflowRun(hit, watched.title);
                this._workflowRunFinished$.next({ runId, workflowId: watched.workflowId, run: hit });
            }
        }
    }

    private _failWorkflowRun(runId: number, error: string): void {
        this._failSandboxTask(`${WORKFLOW_RUN_TASK_PREFIX}${runId}`, this.conversationId ?? 0, error);
        this._unwatchWorkflowRun(runId);
    }

    private _abandonWorkflowRuns(error: string, workflowIds: ReadonlySet<number>): void {
        for (const [runId, watched] of Array.from(this._watchedWorkflowRuns.entries())) {
            if (workflowIds.has(watched.workflowId)) {
                this._failWorkflowRun(runId, error);
            }
        }
    }

    /**
     * Stable per-run key for a pending sandbox card. Events carry a `runId`
     * so N concurrent generations per conversation each get their own row.
     * When `runId` is missing or '' (older/edge events) we degrade to the
     * legacy one-card-per-conversation key, preserving the old behavior.
     */
    private _sandboxRunKey(conversationId: number, runId?: string): string {
        return runId && runId.length > 0 ? runId : `sandbox-${conversationId}`;
    }

    /** Record a pending run key so channel teardown is deferred until it resolves. */
    private _trackPendingSandboxRun(conversationId: number, runKey: string): void {
        const runs = this._pendingSandboxConversationIds.get(conversationId) ?? new Set<string>();
        runs.add(runKey);
        this._pendingSandboxConversationIds.set(conversationId, runs);
    }

    /**
     * Drop a resolved run key; the conversation entry goes away once its last
     * run resolves. If the exact key is unknown (e.g. progress carried a runId
     * but ready/failed didn't — or vice versa) we fall back to clearing the
     * legacy conversation-scoped key so a key mismatch can't hold the channel
     * open forever.
     */
    private _untrackPendingSandboxRun(conversationId: number, runKey: string): void {
        const runs = this._pendingSandboxConversationIds.get(conversationId);
        if (!runs) {
            return;
        }
        if (!runs.delete(runKey)) {
            runs.delete(this._sandboxRunKey(conversationId));
        }
        if (runs.size === 0) {
            this._pendingSandboxConversationIds.delete(conversationId);
        }
    }

    /** Create a run's pending "generating…" card or update its milestone label. */
    private _upsertPendingSandboxCard(sandboxId: string, label: string): void {
        const messages = this._messages$.value;
        const existing = messages.find((m) => m.sandboxId === sandboxId && m.sandboxState === 'generating');
        if (existing) {
            this._messages$.next(messages.map((m) =>
                m.sandboxId === sandboxId && m.sandboxState === 'generating'
                    ? { ...m, sandboxLabel: label }
                    : m,
            ));
            return;
        }
        this._messages$.next([
            ...messages,
            {
                role: 'assistant',
                content: '',
                timestamp: new Date(),
                sandboxState: 'generating',
                sandboxLabel: label,
                sandboxId,
            },
        ]);
    }

    /**
     * Swap the pending card for the finished deliverable card. The card row
     * becomes a normal assistant message carrying a `build_document` toolCard
     * — reusing the exact render path the inline board_summary card uses. If
     * no pending card exists (progress events never arrived) we append a fresh
     * deliverable row so the document still shows up.
     */
    private _resolvePendingSandboxCard(
        sandboxId: string,
        card: GableToolCard['payload'],
    ): void {
        const toolCard: GableToolCard = { tool: 'build_document', payload: card };
        const messages = this._messages$.value;
        const existingIndex = messages.findIndex((m) => m.sandboxId === sandboxId);
        if (existingIndex !== -1) {
            // The telemetry footer (thumbs) should sit under the
            // finished card, not under the preceding text bubble. Move the
            // turn's `interactionId` from the nearest prior assistant text
            // row onto the card row (clearing it there so the footer doesn't
            // render twice), and COPY that row's `messageId` so the thumbs
            // active-state / feedback-panel keying works on the card row.
            // The text row keeps its `messageId` — its thought-trail collapse
            // toggle still needs it. Sandbox card rows are skipped as donors
            // so a second concurrent run can't steal the first card's footer.
            const donorIndex = this._findFooterDonorIndex(messages, existingIndex);
            const donor = donorIndex !== -1 ? messages[donorIndex] : undefined;
            this._messages$.next(messages.map((m, i) => {
                if (i === existingIndex) {
                    return {
                        ...m,
                        sandboxState: undefined,
                        sandboxLabel: undefined,
                        streamState: 'complete' as const,
                        toolCards: [toolCard],
                        interactionId: donor?.interactionId ?? m.interactionId,
                        messageId: donor?.messageId ?? m.messageId,
                    };
                }
                if (i === donorIndex) {
                    return { ...m, interactionId: undefined };
                }
                return m;
            }));
            return;
        }
        // No pending row (progress events never arrived) — append a fresh
        // card row. Same footer-identity transfer, searching back from the
        // end of the list. `sandboxId` is set so the template knows to
        // suppress the assistant mascot on this row too.
        const donorIndex = this._findFooterDonorIndex(messages, messages.length);
        const donor = donorIndex !== -1 ? messages[donorIndex] : undefined;
        const withCleared = donorIndex !== -1
            ? messages.map((m, i) => (i === donorIndex ? { ...m, interactionId: undefined } : m))
            : messages;
        this._messages$.next([
            ...withCleared,
            {
                role: 'assistant',
                content: '',
                timestamp: new Date(),
                streamState: 'complete',
                toolCards: [toolCard],
                sandboxId,
                interactionId: donor?.interactionId,
                messageId: donor?.messageId,
            },
        ]);
    }

    /**
     * Nearest prior assistant row (searching backwards from `beforeIndex`)
     * whose `interactionId` can be moved onto a finished deliverable-card row.
     * Sandbox card rows are excluded — they may already carry a transferred
     * interactionId from an earlier run's resolve. Returns -1 if none exists
     * (e.g. history reload), in which case the card simply has no footer.
     */
    private _findFooterDonorIndex(messages: GableChatMessage[], beforeIndex: number): number {
        for (let i = beforeIndex - 1; i >= 0; i--) {
            const candidate = messages[i];
            if (
                candidate.role === 'assistant'
                && candidate.interactionId !== undefined
                && candidate.sandboxId === undefined
            ) {
                return i;
            }
        }
        return -1;
    }

    /**
     * Convert THIS run's pending card into a soft-failure bubble IN PLACE at
     * its existing index (mirrors the in-place resolve on `ai.sandbox.ready`).
     * Replacing in place — rather than dropping the row and appending a new
     * error bubble at the end — keeps a failed earlier run's error anchored to
     * its own card position when builds resolve out of order. No native dialogs
     * / growler so it stays inline in the conversation. If no pending card
     * exists (progress events never arrived) we append a fresh error bubble.
     */
    private _failPendingSandboxCard(sandboxId: string, message: string): void {
        const friendly = message && message.length > 0
            ? message
            : 'Sorry — I couldn\'t finish building that document. Please try again.';
        const messages = this._messages$.value;
        const existingIndex = messages.findIndex((m) => m.sandboxId === sandboxId);
        if (existingIndex !== -1) {
            this._messages$.next(messages.map((m, i) =>
                i === existingIndex
                    ? {
                        ...m,
                        sandboxState: undefined,
                        sandboxLabel: undefined,
                        content: friendly,
                        streamState: 'failed' as const,
                        streamEndedAt: Date.now(),
                    }
                    : m,
            ));
            return;
        }
        this._messages$.next([
            ...messages,
            {
                role: 'assistant',
                content: friendly,
                timestamp: new Date(),
                streamState: 'failed',
                sandboxId,
            },
        ]);
    }

    /**
     * Tear the channel down once no sandbox generation is still pending AND
     * the live turn has already finished streaming. If a turn is still active
     * (or another conversation's sandbox is pending) we leave the channel
     * alone — the normal completion/teardown paths own it.
     */
    private _maybeTeardownAfterSandbox(): void {
        if (this._pendingSandboxConversationIds.size > 0) {
            return;
        }
        // Only tear down if nothing is actively streaming — otherwise the
        // in-flight turn still needs the channel.
        const stillStreaming = this._messages$.value.some(
            (m) => m.role === 'assistant' && m.streamState === 'streaming',
        );
        if (stillStreaming || this._isSending$.value) {
            return;
        }
        this._teardownStream();
    }

    /**
     * Detach everything that outlives the instance: the `document` listener and
     * its grace timer, the Echo channels, the subscriptions held by other
     * services, and every stream/sandbox timer. Without this a discarded
     * instance keeps reacting to tab visibility, realtime events and org
     * changes — in the unit suite that means an instance from an earlier spec
     * finalizing a "stuck" stream against a later spec's fake clock, against
     * its own now-stubbed collaborators.
     */
    ngOnDestroy(): void {
        if (typeof document !== 'undefined') {
            document.removeEventListener('visibilitychange', this._onVisibilityChange);
        }
        this._clearVisibilityRecoveryTimer();
        // EchoService is its own long-lived singleton and holds the three
        // suggestion handlers bound in _subscribeToSuggestionsChannel, each
        // closing over `this` and each re-firing listPendingProposals. Leaving
        // the channel is what releases them; `document` was only half of it.
        this._unsubscribeFromSuggestionsChannel();
        this._lifetimeSubs.unsubscribe();
        this._teardownStream();
        this._stopWorkflowRunPoll();
        this._clearBusyRetry();
    }

    private _clearVisibilityRecoveryTimer(): void {
        if (this._visibilityRecoveryTimer !== null) {
            clearTimeout(this._visibilityRecoveryTimer);
            this._visibilityRecoveryTimer = null;
        }
    }

    private _armStreamWatchdog(): void {
        this._disarmStreamWatchdog();
        this._streamWatchdog = setTimeout(() => {
            this._streamWatchdog = null;
            this._forceFinalizeStuckStream();
        }, STREAM_WATCHDOG_MS);
    }

    private _disarmStreamWatchdog(): void {
        if (this._streamWatchdog !== null) {
            clearTimeout(this._streamWatchdog);
            this._streamWatchdog = null;
        }
    }

    private _forceFinalizeStuckStream(): void {
        const stuck = this._messages$.value.some(
            (m) => m.role === 'assistant' && m.streamState === 'streaming',
        );
        if (!stuck && !this._isSending$.value) {
            return;
        }
        this._errorReporter.report(
            'stream_stalled',
            `The assistant stream delivered nothing for ${STREAM_WATCHDOG_MS / 1000}s and was force-finalized.`,
            { context: 'gable-drawer:stream-watchdog', conversationId: this.conversationId ?? undefined },
        );
        this._messages$.next(this._messages$.value.map((m) => {
            if (m.role !== 'assistant' || m.streamState !== 'streaming') {
                return m;
            }
            const settledThoughts = (m.thoughts ?? []).map((t) =>
                t.ok === undefined ? { ...t, ok: true } : t,
            );
            return {
                ...m,
                streamState: 'complete' as const,
                streamEndedAt: Date.now(),
                thoughts: settledThoughts,
            };
        }));
        const conversationId = this.conversationId;
        this._isSending$.next(false);
        this._teardownStream();
        this._backfillFromServer(conversationId);
    }

    /** True when nothing has arrived for long enough that the stream cannot be merely slow. */
    private _streamHasGoneSilent(): boolean {
        return Date.now() - this._lastStreamEventAt >= STREAM_SILENCE_MS;
    }

    /**
     * Pull the authoritative transcript after a stream failed to deliver.
     *
     * A turn that completes while the socket is down (hidden tab reclaims its
     * realtime connection after 5 min — SD-7778) leaves the reply on the server and
     * an empty bubble on screen. Finalizing alone renders that emptiness; the
     * answer only appears if we go and get it.
     */
    private _backfillFromServer(conversationId: number | null, attempt: number = 0): void {
        if (conversationId === null) {
            return;
        }
        // Quiet merge, NOT loadConversation(): that wipes the drawer up front, and a
        // transcript fetched before the turn-complete callback persisted the answer
        // came back without the assistant row — replacing the streamed bubble with
        // just the user message, permanently (the old re-poll keyed on a 'streaming'
        // history row, which the transcript contract never reports).
        this._service.loadConversation(conversationId).subscribe({
            next: (res) => {
                // A new chat / conversation switch / scope reset superseded this backfill.
                if (this.conversationId !== conversationId || this._isSending$.value) {
                    return;
                }
                const raw = res.messages ?? [];
                // Key on the LAST assistant row: any earlier answer in a multi-turn
                // conversation would otherwise satisfy the check and strand the new
                // turn as a permanently blank bubble.
                const lastAssistant = [...raw].reverse().find((m) => m.role === 'assistant');
                const lastAnswered =
                    !!lastAssistant && (lastAssistant.content ?? '').trim() !== '';
                const lastFailed =
                    !!lastAssistant &&
                    (lastAssistant.streamState === 'failed' || lastAssistant.streamState === 'cancelled');
                if (!lastAnswered && !lastFailed) {
                    this._scheduleBackfillRepoll(conversationId, attempt);
                    return;
                }
                this._activeTitle$.next(res.conversation.title ?? this._activeTitle$.value);
                const deliverableRows = raw.filter((m) => !!m.sandboxRunId);
                this._messages$.next(
                    raw.filter((m) => !m.sandboxRunId).map((m) => this._mapHistoryMessage(m)),
                );
                this._rehydrateSandboxTasksFromHistory(deliverableRows);
            },
            error: () => {
                // Keep whatever streamed — never trade real content for an error state.
            },
        });
    }

    /** Re-poll the transcript (20s apart, ~15 min horizon); give up VISIBLY, never silently. */
    private _scheduleBackfillRepoll(conversationId: number, attempt: number): void {
        if (attempt >= 45) {
            this._failLastPendingBubble(conversationId);
            return;
        }
        setTimeout(() => {
            if (this.conversationId === conversationId && !this._isSending$.value) {
                this._backfillFromServer(conversationId, attempt + 1);
            }
        }, 20_000);
    }

    /** Identifies one send attempt, so a retry does not create a second turn (SD-10033). */
    private _randomTurnId(): string {
        const c = window.crypto;
        if (c && typeof c.randomUUID === 'function') {
            return c.randomUUID();
        }
        return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    }

    /** Turn the stranded empty bubble into an explicit failure the user can see. */
    private _failLastPendingBubble(conversationId: number): void {
        if (this.conversationId !== conversationId) {
            return;
        }
        const messages = this._messages$.value;
        const last = messages[messages.length - 1];
        if (!last || last.role !== 'assistant' || (last.content ?? '').trim() !== '') {
            return;
        }
        this._messages$.next([
            ...messages.slice(0, -1),
            { ...last, content: GABLE_TURN_FAILED_COPY, streamState: 'failed' as const },
        ]);
    }

    /**
     * Arm (or re-arm) the per-run sandbox watchdog. Each progress milestone
     * resets the clock; if no terminal event lands within
     * `SANDBOX_WATCHDOG_MS` we soft-fail the pending card in place. Capturing
     * `conversationId` here lets the expiry path also untrack the run so the
     * channel can tear down.
     */
    private _armSandboxWatchdog(conversationId: number, runKey: string): void {
        this._disarmSandboxWatchdog(runKey);
        const timer = setTimeout(() => {
            this._sandboxWatchdogs.delete(runKey);
            this._expireSandboxWatchdog(conversationId, runKey);
        }, SANDBOX_WATCHDOG_MS);
        this._sandboxWatchdogs.set(runKey, timer);
    }

    private _disarmSandboxWatchdog(runKey: string): void {
        const timer = this._sandboxWatchdogs.get(runKey);
        if (timer !== undefined) {
            clearTimeout(timer);
            this._sandboxWatchdogs.delete(runKey);
        }
    }

    /** Clear every pending sandbox watchdog timer (teardown / new conversation). */
    private _clearAllSandboxWatchdogs(): void {
        this._sandboxWatchdogs.forEach((timer) => clearTimeout(timer));
        this._sandboxWatchdogs.clear();
    }

    /**
     * No terminal event landed in time — the Lambda likely died (DLQ'd) without
     * an `ai.sandbox.failed`. Convert the still-pending card to a soft-failure
     * in place (same position) and stop deferring teardown for this run.
     */
    private _expireSandboxWatchdog(_conversationId: number, runKey: string): void {
        const task = this._sandboxTasks$.value.find((t) => t.runKey === runKey);
        if (!task || task.state !== 'running') {
            return;
        }
        // Option B: NEVER hard-fail a long task on the watchdog — delivery is
        // reliable, just minutes-long. Flag it "taking longer than usual" and
        // keep the run tracked so the eventual ai.sandbox.ready still resolves it
        // (no untrack / teardown → the channel stays open to receive it).
        this._markSandboxTaskSlow(runKey);
    }

    private _teardownStream(): void {
        this._awaitingFirstTitle = false;
        if (this._delayedTeardownTimer !== null) {
            clearTimeout(this._delayedTeardownTimer);
            this._delayedTeardownTimer = null;
        }
        if (this._streamSub) {
            this._streamSub.unsubscribe();
            this._streamSub = null;
        }
        if (this._streamHandle) {
            this._streamHandle.close();
            this._streamHandle = null;
        }
        this._disarmStreamWatchdog();
        this._stopStallPoll();
        // The channel is gone, so no terminal sandbox event can land for any
        // still-pending run — drop their watchdog timers so none leak.
        this._clearAllSandboxWatchdogs();
    }

    /**
     * Schedule teardown after a short grace period. Lets late-arriving
     * post-completion events (currently `ai.stream.follow-ups`) land on
     * the channel before we leave it. If the user sends another message
     * during the grace window, `sendMessage` calls `_teardownStream`
     * immediately, which cancels the pending timer and re-opens cleanly.
     */
    private _delayedTeardownTimer: ReturnType<typeof setTimeout> | null = null;

    private _scheduleDelayedTeardown(delayMs: number): void {
        if (this._delayedTeardownTimer !== null) {
            clearTimeout(this._delayedTeardownTimer);
        }
        this._delayedTeardownTimer = setTimeout(() => {
            this._delayedTeardownTimer = null;
            // Re-check at FIRE time, not schedule time: a sandbox generation
            // may have announced itself (ai.sandbox.progress 'queued') in the
            // window since this timer was armed — tearing down now would close
            // the channel its ready/failed event still needs. Teardown then
            // happens in _maybeTeardownAfterSandbox once it resolves.
            if (this._pendingSandboxConversationIds.size > 0) {
                return;
            }
            // Never close the channel under a turn that is still streaming; look again later.
            // The stream watchdog bounds this: a turn that never ends is failed and torn down there.
            if (this._isSending$.value) {
                this._scheduleDelayedTeardown(delayMs);
                return;
            }
            this._teardownStream();
        }, delayMs);
    }

    // -------------------------------------------------------------------------
    // Questions / proposals / feedback
    // -------------------------------------------------------------------------

    answerQuestions(answers: GableQuestionAnswer[], summary: string | null): void {
        const batch = this._pendingQuestions$.value;
        const conversationId = this.conversationId;
        if (batch === null || conversationId === null || this.submittingAnswers) {
            return;
        }
        this.submittingAnswers = true;
        if (summary && summary.length > 0) {
            this._messages$.next([
                ...this._messages$.value,
                { role: 'user', content: summary, timestamp: new Date() },
            ]);
        }
        this._isSending$.next(true);
        // A fresh submit (as opposed to a busy auto-resend) always starts its own retry ladder.
        this._clearBusyRetry();
        this._busyRetryAttempt = 0;
        this._submitAnswers(batch, answers, conversationId);
    }

    /**
     * One POST attempt for an answered question batch — /answer-questions now returns the same
     * chat-stream handshake shapes `chatStream()` does (SD-10526), so this follows the identical
     * streaming / busy / daily-limit / synchronous-failure handling as `_attemptSend`.
     */
    private _submitAnswers(
        batch: GablePendingQuestions,
        answers: GableQuestionAnswer[],
        conversationId: number,
    ): void {
        this._service.answerQuestions({
            conversationId,
            batchId: batch.batchId,
            answers,
            questionsEcho: batch.questions,
        }).subscribe({
            next: (handshake) => {
                this.submittingAnswers = false;
                this._busyRetryAttempt = 0;
                this.conversationId = handshake.conversationId;
                this._pendingQuestions$.next(null);
                if (handshake.streamState === 'failed') {
                    this._isSending$.next(false);
                    this._pushFailedMessage(handshake.message || GABLE_TURN_FAILED_COPY);
                    return;
                }
                if (handshake.title && this._activeTitle$.value === null) {
                    this._activeTitle$.next(handshake.title);
                }
                this._beginStreamingTurn(handshake.conversationId, handshake.assistantMessageId);
            },
            error: (err: unknown) => {
                // Org/day spend cap (SD-10526) — not retryable, unlike gable_busy.
                if (err instanceof HttpErrorResponse && err.status === 429 && err.error?.error === 'gable_daily_limit') {
                    this.submittingAnswers = false;
                    this._isSending$.next(false);
                    this._pushFailedMessage(
                        typeof err.error?.message === 'string' ? err.error.message : GABLE_DAILY_LIMIT_COPY,
                    );
                    return;
                }
                // Same box-at-capacity contract as a chat send: auto-resend the SAME answer
                // batch, targeting whatever conversationId the 503 body names.
                if (err instanceof HttpErrorResponse && err.status === 503 && err.error?.error === 'gable_busy') {
                    this._isSending$.next(false);
                    this._busyRetryAttempt += 1;
                    if (this._busyRetryAttempt >= GABLE_BUSY_MAX_ATTEMPTS) {
                        this._busyRetryAttempt = 0;
                        this.submittingAnswers = false;
                        this._pushFailedMessage(GABLE_BUSY_FINAL_COPY);
                        return;
                    }
                    const retryAfterSeconds = err.error?.retryAfterSeconds;
                    const seconds = typeof retryAfterSeconds === 'number' && retryAfterSeconds > 0
                        ? retryAfterSeconds
                        : GABLE_BUSY_DEFAULT_RETRY_SECONDS;
                    const busyConversationId = typeof err.error?.conversationId === 'number' ? err.error.conversationId : conversationId;
                    this._armBusyRetry(seconds, () => this._submitAnswers(batch, answers, busyConversationId));
                    return;
                }
                this.submittingAnswers = false;
                this._isSending$.next(false);
                const status = err instanceof HttpErrorResponse ? err.status : 0;
                if (status === 422) {
                    this._pendingQuestions$.next(null);
                }
                const structuredMessage = err instanceof HttpErrorResponse && typeof err.error?.message === 'string'
                    ? err.error.message
                    : null;
                this._pushFailedMessage(status === 422
                    ? 'These questions are no longer valid — please refresh or send a new message.'
                    : (structuredMessage ?? 'Sorry — I couldn\'t submit those answers. Please try again.'));
            },
        });
    }

    closeQuestionCard(): void {
        if (this.submittingAnswers) {
            return;
        }
        this._pendingQuestions$.next(null);
    }

    updateThoughtsCollapsed(messageId: number, collapsed: boolean): void {
        this._messages$.next(this._messages$.value.map((m) =>
            m.role === 'assistant' && m.messageId === messageId
                ? { ...m, thoughtsCollapsed: collapsed }
                : m,
        ));
    }

    setFeedback(messageId: number, feedback: GableFeedback): void {
        this._feedbackByMessageId.set(messageId, feedback);
        this._feedback$.next(new Map(this._feedbackByMessageId));
    }

    getFeedback(messageId: number | undefined): GableFeedback | null {
        if (messageId === undefined) {
            return null;
        }
        return this._feedbackByMessageId.get(messageId) ?? null;
    }

    recordFeedback(interactionId: number, feedback: GableFeedback): Observable<unknown> {
        return this._service.recordFeedback(interactionId, feedback);
    }

    // ----- "Feedback submitted" lockout — once the user picks a chip + Send,
    // we collapse the thumbs/panel into a green confirmation and block
    // further feedback on this interaction. Session-only on purpose:
    // a reload should let them re-feedback if they really want.

    private _feedbackSentInteractionIds = new Set<number>();

    markFeedbackSent(interactionId: number): void {
        this._feedbackSentInteractionIds.add(interactionId);
    }

    wasFeedbackSent(interactionId: number | undefined): boolean {
        return interactionId !== undefined && this._feedbackSentInteractionIds.has(interactionId);
    }

    approveProposal(id: number, idempotencyKey: string) {
        // Generic record-edit lane commits here, not through commitActionProposal — same page refresh (SD-8019).
        return this._service.approveProposal(id, idempotencyKey, this._proposalOrgId(id)).pipe(
            tap({ next: () => this._actionCommitted$.next(), error: (err) => this._growlOnProposalRateLimit(err) }),
        );
    }

    rejectProposal(id: number, reason?: GableDismissReason) {
        return this._service.rejectProposal(id, reason, this._proposalOrgId(id)).pipe(
            tap({ error: (err) => this._growlOnProposalRateLimit(err) }),
        );
    }

    /** The gable-proposals rate limiter's plain 429 reads as a raw error without this (SD-10526). */
    private _growlOnProposalRateLimit(err: unknown): void {
        if (err instanceof HttpErrorResponse && err.status === 429) {
            this._growler.error('Gable', GABLE_RATE_LIMITED_COPY);
        }
    }

    /** Row's own org for per-item actions in PMC scope (SD-9074 wave 2); undefined = jwt org. */
    private _proposalOrgId(proposalId: number): number | undefined {
        if (!this._scope.isPmcScoped) {
            return undefined;
        }
        return this._pendingProposals$.value.find((p) => p.id === proposalId)?.organizationId;
    }

    /**
     * Snooze a pending proposal until the next morning (thin pass-through).
     * The Activity panel drives the queue-removal + toast + feed reload once
     * this resolves.
     */
    snoozeProposal(id: number) {
        return this._service.snoozeProposal(id, this._proposalOrgId(id)).pipe(
            tap({ error: (err) => this._growlOnProposalRateLimit(err) }),
        );
    }

    commitActionProposal(id: number, idempotencyKey: string, edits: ActionPreviewEdits) {
        // No chat context, and so no post-commit follow-ups job: a message carrying a proposal
        // never renders chips, so asking for them cost a model call and held the channel open
        // 45s for a broadcast with nowhere to land.
        return this._service.commitActionProposal(id, idempotencyKey, edits, undefined, this._proposalOrgId(id)).pipe(
            // Tell the app shell a write landed so it can refresh the page
            // behind the drawer (SD-8019). Emits only on success; errors pass
            // through untouched to the caller.
            tap({ next: () => this._actionCommitted$.next(), error: (err) => this._growlOnProposalRateLimit(err) }),
        );
    }

    /**
     * (Re)open the conversation's realtime channel WITHOUT starting a turn —
     * used when a late broadcast is expected outside a streaming window
     * (e.g. post-commit follow-up chips). No-op while a stream is live;
     * otherwise opens the channel and schedules a normal delayed teardown.
     */
    private _ensureChannelOpen(conversationId: number, graceMs: number): void {
        if (this._streamHandle !== null) {
            // Already listening — just extend the teardown grace window.
            this._scheduleDelayedTeardown(graceMs);
            return;
        }
        const handle = this._streamService.open(conversationId);
        this._streamHandle = handle;
        this._streamSub = handle.events$.subscribe({
            next: (event) => this._handleStreamEvent(event),
        });
        this._scheduleDelayedTeardown(graceMs);
    }

    refineActionProposal(id: number, utilityActionId: string, edits: ActionPreviewEdits) {
        return this._service.refineActionProposal(id, utilityActionId, edits, this._proposalOrgId(id)).pipe(
            tap({ error: (err) => this._growlOnProposalRateLimit(err) }),
        );
    }

    /**
     * Patch the final state of an action-preview tool card (committed /
     * cancelled). The card component reads `payload.state` in ngOnChanges
     * and shows the matching "Action confirmed." / "Action cancelled."
     * footer. Decoupling from ViewChildren means the UI survives whichever
     * change-detection cycle happens to run first.
     */
    patchActionPreviewState(
        proposalId: number,
        state: 'pending' | 'committing' | 'committed' | 'cancelled',
        result: Record<string, unknown> | null = null,
    ): void {
        const messages = this._messages$.value.map((msg) => {
            if (!msg.toolCards || msg.toolCards.length === 0) {
                return msg;
            }
            let touched = false;
            const nextCards = msg.toolCards.map((card) => {
                if (card.tool !== 'propose_action' && card.tool !== 'propose_crud_change') {
                    return card;
                }
                const payload = card.payload as { proposal_id?: number; proposalId?: number } | null;
                const id = payload?.proposal_id ?? payload?.proposalId;
                if (id !== proposalId) {
                    return card;
                }
                touched = true;
                const next: Record<string, unknown> = {
                    ...(card.payload as Record<string, unknown>),
                    state,
                };
                if (result !== null) {
                    // Mirror the execute() envelope under `result` so the
                    // collapsed card can render counts / deeplinks without
                    // another round trip.
                    next['result'] = result;
                }
                return { ...card, payload: next };
            });
            return touched ? { ...msg, toolCards: nextCards } : msg;
        });
        this._messages$.next(messages);
    }

    /**
     * Patch the in-memory action-preview tool card on whichever assistant
     * message holds it. Used by the drawer-host after a successful /refine
     * round-trip — backend rewrote `preview` + `edits`, we swap them into
     * the existing toolCard payload so the OnPush card re-renders.
     */
    patchActionPreview(
        proposalId: number,
        next: { preview: unknown; edits: unknown },
    ): void {
        const messages = this._messages$.value.map((msg) => {
            if (!msg.toolCards || msg.toolCards.length === 0) {
                return msg;
            }
            let touched = false;
            const nextCards = msg.toolCards.map((card) => {
                if (card.tool !== 'propose_action' && card.tool !== 'propose_crud_change') {
                    return card;
                }
                const payload = card.payload as { proposal_id?: number; proposalId?: number } | null;
                const id = payload?.proposal_id ?? payload?.proposalId;
                if (id !== proposalId) {
                    return card;
                }
                touched = true;
                return {
                    ...card,
                    payload: {
                        ...(card.payload as Record<string, unknown>),
                        preview: next.preview,
                        // edits are not part of the rendered payload but the
                        // backend echoes them; keep around for diagnostics.
                        edits: next.edits,
                    },
                };
            });
            return touched ? { ...msg, toolCards: nextCards } : msg;
        });
        this._messages$.next(messages);
    }

    removeProposal(id: number): void {
        this._pendingProposals$.next(this._pendingProposals$.value.filter((p) => p.id !== id));
    }

    appendMessage(msg: GableChatMessage): void {
        this._messages$.next([...this._messages$.value, msg]);
    }

    /**
     * Pusher silently drops events over ~10KB, which means a full report
     * payload can arrive in `ai.stream.completed` with `toolCards: []`. When
     * we detect that case (a card-producing tool ran but no cards came
     * through), pull the persisted cards from the message's metadata via
     * REST and patch them into the assistant bubble.
     */
    /**
     * Reconcile a finished bubble with what core saved: cards the broadcast could not
     * carry, and the final text. The saved text wins over what streamed — a lost delta
     * or a line core withheld both show up as a mismatch here.
     */
    private _reconcileMessageFromServer(conversationId: number, messageId: number, mergeCards: boolean): void {
        this._service.fetchToolCards(conversationId, messageId).subscribe({
            next: (res) => {
                const cards = mergeCards ? (res.toolCards ?? []) : [];
                const saved = (res.content ?? '').trim();
                this._messages$.next(this._messages$.value.map((m) => {
                    if (m.role !== 'assistant' || m.messageId !== messageId) {
                        return m;
                    }
                    const content = saved !== '' && saved !== (m.content ?? '').trim() ? saved : m.content;
                    const known = new Set((m.toolCards ?? []).map((c) => JSON.stringify(c)));
                    const fresh = cards.filter((c) => !known.has(JSON.stringify(c)));
                    return fresh.length > 0
                        ? { ...m, content, toolCards: [...(m.toolCards ?? []), ...fresh] }
                        : { ...m, content };
                }));
            },
            error: () => {
                // Non-fatal: the user can re-ask if the card never lands.
            },
        });
    }

}
